import { flushClassBoards } from '../../utils/classBoardPersistence';
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaTimes, FaExpand, FaCompress,
  FaRegCopy, FaShareAlt, FaCircle, FaCog, FaEye, FaFilePowerpoint  } from 'react-icons/fa';
import io from 'socket.io-client';
import { Device } from 'mediasoup-client';
import { useSearchParams } from 'react-router-dom';
import MediaSoupHandler from '../../utils/MediaSoupHandler';
import {
  setRoom,
  clearRoom,
  addParticipant,
  updateParticipantStatus,
  addChatMessage,
  setLocalParticipantStatus,
  updateParticipant,
  updateParticipantNetworkStats,
  removeParticipantNetworkStats,
  removeParticipant,
  setNewHost
  
} from '../../store/slices/roomSlice';
import {
  setLocalStream,
  clearMedia,
  toggleAudio,
  toggleVideo,
  toggleRecording,
  toggleChatStatus,
  addRemoteScreen,
  removeRemoteScreen,
} from '../../store/slices/mediaSlice';
import api,{
  getRoomProducers,
  refreshAuthSession,
} from '../../utils/api';
import VideoGrid from './VideoGrid';
import Controls from './Controls';
// Muted-speech alerts are disabled at the user's request. To re-enable later,
// uncomment this import AND the ClassroomDiscipline JSX block near Controls.
// import ClassroomDiscipline from './ClassroomDiscipline';
import ClassBlackboard from './ClassBlackboard';
import BlackboardParticipantDock from './BlackboardParticipantDock';
import MeetingEndCountdownAlert from './MeetingEndCountdownAlert';
import ScreenShareParticipantMonitor from './ScreenShareParticipantMonitor';
import LocalPresentationStudio from './presentation/LocalPresentationStudio';
import { usePresentationSync } from './presentation/usePresentationSync';
import Chat from './Chat';
import Participants from './Participants';
import BiblePanel from './BiblePanel';
import { toast } from 'react-toastify';
import { store } from './../../store/store'; 
import JoiningInfo from './JoiningInfo';
import ShowMore from './showmore';
import isEqual from 'lodash/isEqual';
import { useNavigate } from 'react-router-dom';
import RecordingIndicator from '../shared/Recording-indicator';
import AutomaticRecordingControl from './AutomaticRecordingControl';
import { selectAdmittedParticipants } from './../../selectors/mediaSelectors';
import ParticipantStatusOverlay from './ParticipantStatusOverlay';
import logo from './../../assets/images/logo192.png'
import {registerPiPBridge, enterPiPFor, isMobile, exitPiPIf } from '../../utils/pip';
import { onChatMessage } from '../../notifications/handlers';
import { isNative } from './../../utils/platform';
import { showAlert, showConfirm } from '../../store/slices/uiSlice';
import { FaSyncAlt } from 'react-icons/fa';
import THREEClassroom from './3DClassroom';
import {
  clearMeetingRuntimeStorage,
  getOrCreateMeetingDeviceId,
  getStoredAccessToken,
  hydrateMeetingIdentity,
  persistRoomAccess,
} from '../../utils/meetingAuth';
import { getMeetingSocketBaseUrl } from '../../utils/meetingSocketUrl';
import {
  installMeetingAudioUnlock,
  playJoinRequestSound,
} from '../../utils/meetingJoinSound';
import meetingRecorder from '../../utils/meetingRecorder';
import { pendingDirectRecordings } from '../../utils/directRecordingUpload';
import { getMeetingDisplayName } from '../../utils/meetingDisplayName';
import {
  createMeetingAudioConstraints,
  getMeetingAudioConstraintAttempts,
  getStoredMeetingAudioMode,
  prepareMeetingAudioTrack,
} from '../../utils/meetingAudio';
import {
  getMeetingEndWarningKey,
  persistMeetingEndWarning,
  readMeetingEndWarning,
  shouldShowThreeMinuteWarning,
} from '../../utils/meetingEndCountdown';
import {
  classifyMediaApiError,
  classifyMeetingJoinFailure,
  resolveInitialMeetingPublishAccess,
} from '../../utils/mediaReliability';
import {
  canDismissRecordingPrompt,
  normalizeMeetingRole,
  shouldShowClassroomRecordingPrompt,
} from '../../utils/recordingPromptPolicy';
import { RECORDING_UI_ENABLED } from '../../config/meetingFeatureFlags';
import { notifyRecordingStop, recordingUiError } from '../../utils/recordingControls';

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const MEETING_VIEW_MODE_KEY = 'meeting.viewMode.v2';
const MEETING_VIEW_MODES = new Set(['spotlight', 'tiles', 'sidebar']);

const MeetingLiveClock = ({ compact = false }) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const time = useMemo(
    () =>
      new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      }).format(now),
    [now]
  );

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border border-emerald-300/20 bg-slate-950/45 font-mono text-emerald-200 shadow-inner ${
        compact ? 'px-2.5 py-1 text-xs font-semibold' : 'px-3.5 py-2 text-sm font-bold'
      }`}
      title="Live Indian Standard Time"
      aria-label={`Live time ${time} Indian Standard Time`}
    >
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
      {time}
      <span className={compact ? 'text-[10px] text-emerald-100/70' : 'text-xs text-emerald-100/70'}>IST</span>
    </span>
  );
};

const parseClock = (value) => {
  const match = String(value || '')
    .trim()
    .toUpperCase()
    .match(/^(\d{1,2}):(\d{2})(?:\s*([AP]M))?$/);
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = match[3] || '';
  if (minutes > 59 || hours > (meridiem ? 12 : 23)) return null;
  if (meridiem === 'PM' && hours !== 12) hours += 12;
  if (meridiem === 'AM' && hours === 12) hours = 0;
  return { hours, minutes };
};

const getPeriodEndTimestamp = (room) => {
  const absoluteEnd = Number(new Date(
    room?.scheduledEndAt || room?.endAt || 0
  ));
  if (Number.isFinite(absoluteEnd) && absoluteEnd > 0) return absoluteEnd;

  const startClock = parseClock(
    room?.periodStartTime ||
      room?.timetableMetadata?.startTime24 ||
      room?.timetableMetadata?.startTime ||
      room?.startTime
  );
  const clock = parseClock(
    room?.periodEndTime ||
      room?.timetableMetadata?.endTime24 ||
      room?.timetableMetadata?.endTime ||
      room?.endTime
  );
  if (!clock) return 0;

  // Anchor the timetable date to the room's generated/scheduled date. This is
  // stable across browser time zones and correctly handles a period that ends
  // after midnight (for example 11:30 PM–1:00 AM).
  const anchorValue = Number(new Date(room?.scheduledAt || room?.generatedAt || Date.now()));
  const anchorInIst = new Date(
    (Number.isFinite(anchorValue) ? anchorValue : Date.now()) + IST_OFFSET_MS
  );
  let endTimestamp = (
    Date.UTC(
      anchorInIst.getUTCFullYear(),
      anchorInIst.getUTCMonth(),
      anchorInIst.getUTCDate(),
      clock.hours,
      clock.minutes,
      0,
      0
    ) - IST_OFFSET_MS
  );
  if (
    startClock &&
    clock.hours * 60 + clock.minutes <=
      startClock.hours * 60 + startClock.minutes
  ) {
    endTimestamp += 24 * 60 * 60 * 1000;
  }
  return endTimestamp;
};

const getPeriodStartTimestamp = (room) => {
  const clock = parseClock(
    room?.periodStartTime ||
      room?.timetableMetadata?.startTime24 ||
      room?.timetableMetadata?.startTime ||
      room?.startTime
  );
  if (!clock) return Number(new Date(room?.generatedAt || 0)) || Date.now();
  const anchorValue = Number(new Date(
    room?.scheduledAt || room?.generatedAt || Date.now()
  ));
  const nowInIst = new Date(
    (Number.isFinite(anchorValue) ? anchorValue : Date.now()) + IST_OFFSET_MS
  );
  return Date.UTC(
    nowInIst.getUTCFullYear(),
    nowInIst.getUTCMonth(),
    nowInIst.getUTCDate(),
    clock.hours,
    clock.minutes,
    0,
    0
  ) - IST_OFFSET_MS;
};

const dashboardUrlFor = (user) => {
  const role = String(user?.role || sessionStorage.getItem('userRole') || '').toLowerCase();
  if (role === 'faculty') return 'https://shepherdsindia.sbs/user/dashboard-faculty';
  if (role === 'student') return 'https://shepherdsindia.sbs/user/dashboard-student';
  if (role === 'staff') return 'https://shepherdsindia.sbs/user/dashboard-staff';
  if (role === 'visitor' || role === 'visitors') {
    return 'https://shepherdsindia.sbs/user/dashboard-visitors';
  }
  return 'https://shepherdsindia.sbs/user/dashboard-main';
};

const closeOrReturnToDashboard = (user) => {
  const fallback = dashboardUrlFor(user);
  if (window.opener && !window.opener.closed) {
    window.close();
    window.setTimeout(() => window.location.replace(fallback), 400);
    return;
  }
  window.location.replace(fallback);
};

const formatRemaining = (seconds) => {
  const value = Math.max(0, Number(seconds) || 0);
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const secs = value % 60;
  return `${hours ? `${String(hours).padStart(2, '0')}:` : ''}${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const emitWithAckTimeout = (socket, eventName, payload, timeoutMs = 12000) =>
  new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Meeting socket is not connected'));
      return;
    }
    const timer = window.setTimeout(() => {
      reject(new Error(`${eventName} acknowledgement timed out`));
    }, timeoutMs);
    socket.emit(eventName, payload, (response) => {
      window.clearTimeout(timer);
      resolve(response || {});
    });
  });

const preferredMediaConstraints = () => {
  const audioInputId = localStorage.getItem('meeting.audioInputId') || '';
  const videoInputId = localStorage.getItem('meeting.videoInputId') || '';
  const audioMode = getStoredMeetingAudioMode();
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const lowNetwork = connection?.saveData === true ||
    ['slow-2g', '2g'].includes(String(connection?.effectiveType || ''));
  return {
    audioMode,
    audio: createMeetingAudioConstraints({
      mode: audioMode,
      deviceId: audioInputId,
      exact: Boolean(audioInputId),
    }),
    video: {
      ...(videoInputId ? { deviceId: { exact: videoInputId } } : {}),
      width: { ideal: lowNetwork ? 320 : 640, max: lowNetwork ? 480 : 1280 },
      height: { ideal: lowNetwork ? 180 : 360, max: lowNetwork ? 270 : 720 },
      frameRate: { ideal: lowNetwork ? 8 : 15, max: lowNetwork ? 10 : 20 },
      facingMode: { ideal: 'user' },
    },
  };
};

// Camera and microphone are acquired independently. A missing/blocked camera
// must never stop the user from hearing the room, and a missing microphone
// must never prevent camera video from joining on mobile browsers.
const acquireMeetingMedia = async ({
  audioEnabled = true,
  videoEnabled = true,
} = {}) => {
  // A participant who intentionally joins with both devices off needs no
  // permission prompt and still gets a valid empty MediaStream for receive
  // transport setup.
  if (!audioEnabled && !videoEnabled) {
    return new MediaStream();
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('This browser does not support camera or microphone access');
  }
  const preferred = preferredMediaConstraints();
  const audioMode = preferred.audioMode || getStoredMeetingAudioMode();
  const audioInputId = localStorage.getItem('meeting.audioInputId') || '';
  let audioTrack = null;
  let videoTrack = null;

  if (audioEnabled) {
    const audioAttempts = [
      preferred.audio,
      ...getMeetingAudioConstraintAttempts(audioMode, audioInputId).slice(1),
    ];
    for (const audio of audioAttempts) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio, video: false });
        audioTrack = stream.getAudioTracks()[0] || null;
        if (audioTrack) {
          await prepareMeetingAudioTrack(audioTrack, audioMode);
          break;
        }
      } catch (error) {
        if (error?.name === 'NotAllowedError') break;
      }
    }
  }

  const videoAttempts = [preferred.video, {
    width: { ideal: 320, max: 640 },
    height: { ideal: 180, max: 360 },
    frameRate: { ideal: 8, max: 12 },
    facingMode: { ideal: 'user' },
  }, true];
  if (videoEnabled) {
    for (const video of videoAttempts) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: false, video });
        videoTrack = stream.getVideoTracks()[0] || null;
        if (videoTrack) {
          try { videoTrack.contentHint = 'motion'; } catch {}
          break;
        }
      } catch (error) {
        if (error?.name === 'NotAllowedError') break;
      }
    }
  }

  if (audioEnabled && videoEnabled && !audioTrack && !videoTrack) {
    toast.warning('Camera and microphone are unavailable. You can still receive the meeting.');
  } else if (audioEnabled && !audioTrack) {
    toast.warning('Microphone unavailable; joined with camera only.');
  } else if (videoEnabled && !videoTrack) {
    toast.warning('Camera unavailable; joined with microphone only.');
  }
  return new MediaStream([audioTrack, videoTrack].filter(Boolean));
};

const MeetingRoom = () => {


  const { roomId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const participantsGet = useSelector((state) => state.room || []);
  const meetingContainerRef = useRef(null);
  const controlsBarRef = useRef(null);
  const meetingIdentity = hydrateMeetingIdentity();
  const sessionId = meetingIdentity.sessionId;
  // Media REST, Socket.IO and the same-tab lock must always use one exact
  // participant session even when the login flow originally persisted it in
  // localStorage.
  if (sessionId) sessionStorage.setItem('sessionId', sessionId);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [lastIncomingChat, setLastIncomingChat] = useState(null);
  const [requestedChatTarget, setRequestedChatTarget] = useState(null);
  const unreadRefreshSequenceRef = useRef(0);
const [pendingRequests, setPendingRequests] = useState(0);
  const userIdGet = meetingIdentity.userId;
  const [floatingReactions, setFloatingReactions] = useState([]);
  const recording = useSelector((state) => state.media.recording);
  const [showMeetingInfo, setShowMeetingInfo] = useState(false);
const [showMoreMenu, setShowMoreMenu] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const currentRoom = useSelector((state) => state.room.currentRoom);
  const localStream = useSelector((state) => state.media.localStream);
  const participantStatusFromStore = useSelector(
    (state) => state.room.localParticipantStatus
  );
  const videoEnabled = useSelector((state) => state.media.videoEnabled);
  const audioEnabled = useSelector((state) => state.media.audioEnabled);
  const screenSharing = useSelector((state) => state.media.screenSharing);
 const participantStatus =
   typeof participantStatusFromStore === 'string'
     ? participantStatusFromStore
     : sessionStorage.getItem('statusget');
  const [socket, setSocket] = useState(null);
  const socketRef = useRef(null);
  const initStartedRef = useRef(false);
  const [remoteVideoStatus, setRemoteVideoStatus] = useState({});
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showBible, setShowBible] = useState(false);
  const [showBlackboard, setShowBlackboard] = useState(false);
  const [showLocalPresentation, setShowLocalPresentation] = useState(false);
  const [blackboardOpenRequestId, setBlackboardOpenRequestId] = useState(0);
  const [blackboardMinimizeRequestId, setBlackboardMinimizeRequestId] = useState(0);
  const [remoteStreamState, setRemoteStreamState] = useState({});
  const mediaSoupHandler = useRef(null);
  const participantExitEventsRef = useRef(new Set());
  const producerSyncPromiseRef = useRef(null);
  const mediaRecoveryPromiseRef = useRef(null);
  const participantRetryPromisesRef = useRef(new Map());
  const remoteMediaHealthRef = useRef({});
  const lastHealthEmitRef = useRef(new Map());
  const [videoDeliveryReports, setVideoDeliveryReports] = useState({});
  const [audioDeliveryReports, setAudioDeliveryReports] = useState({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [remoteScreens, setRemoteScreens] = useState({});
  // Socket.IO sends both `newProducer` and `screen-sharing` for the same
  // screen producer.  Transport recovery can also close an older producer
  // after its replacement is already live.  Keep the newest producer per
  // session outside React state so those duplicate/out-of-order events cannot
  // repeatedly remove and re-add the remote screen (the visible page blink).
  const remoteScreenStateRef = useRef(new Map());
  const remoteProducerStateRef = useRef(new Map());
  const socketRecoveryPromiseRef = useRef(null);
  const socketAuthRefreshPromiseRef = useRef(null);
  const mediaStateVersionRef = useRef(new Map());
  const suppressNextMediaSyncRef = useRef(new Set());
  const localMediaStateRef = useRef({
    audio: audioEnabled,
    video: videoEnabled,
  });
  // Keep the pre-join choice even while a participant is waiting. Waiting
  // users receive an empty stream and no device permission prompt; these
  // values are applied only after the socket confirms admission.
  const desiredMediaStateRef = useRef({
    audioEnabled: audioEnabled !== false,
    videoEnabled: videoEnabled !== false,
  });
  const autoRecordStopTimerRef = useRef(null);
  const threeMinuteAlertKeyRef = useRef('');
  const memoizedRemoteStreams = useMemo(() => remoteStreamState, [remoteStreamState]);
  const [reactions, setReactions] = useState([]);
  const [frequency, setFrequency]= useState(0);
  const [isRejoining, setIsRejoining] = useState(false);
  const [periodRemaining, setPeriodRemaining] = useState(null);
  const [threeMinuteWarning, setThreeMinuteWarning] = useState(null);
  const [viewMode, setViewMode] = useState(
    () => {
      const stored = localStorage.getItem(MEETING_VIEW_MODE_KEY);
      return MEETING_VIEW_MODES.has(stored) ? stored : 'sidebar';
    }
  );
  const [recordPrompt, setRecordPrompt] = useState(null);
  const [recordPromptBusy, setRecordPromptBusy] = useState(false);
  const [recordingRequiredSignal, setRecordingRequiredSignal] = useState(null);
  const dismissedRecordingPromptsRef = useRef(new Set());
  const recordingStateRef = useRef(Boolean(recording));
  const [extensionOffer, setExtensionOffer] = useState(null);
  const [extensionBusy, setExtensionBusy] = useState(false);
  const [visibleMeetingObservers, setVisibleMeetingObservers] = useState([]);
   const mainVideoRef = useRef(null); 
  const activeConnections = useRef({
    producers: 0,
    consumers: 0
  });

  const localPresentationStatus = String(participantStatus || '').toLowerCase();
  const localPresentationAccess = currentRoom?.access || {};
  const hasPresentationModeratorAccess = [
    localPresentationAccess.canManageMedia,
    localPresentationAccess.canRecord,
    localPresentationAccess.canAdmit,
    localPresentationAccess.canEndMeeting,
  ].some((value) => value === true);
  const canUseLocalPresentation =
    localPresentationStatus === 'presenting' ||
    (localPresentationStatus === 'admitted' && hasPresentationModeratorAccess);
  const {
    remotePresentation,
    presentationClearRevision,
    presentationSyncError,
    publishPresentationState,
  } = usePresentationSync({
    socket,
    roomId: currentRoom?.roomId || roomId,
    sessionId,
    canPublish: canUseLocalPresentation,
  });
  const dismissThreeMinuteWarning = useCallback(
    () => setThreeMinuteWarning(null),
    []
  );

  useEffect(() => {
    if (!canUseLocalPresentation) setShowLocalPresentation(false);
  }, [canUseLocalPresentation]);

  useEffect(() => {
    desiredMediaStateRef.current = {
      audioEnabled: audioEnabled !== false,
      videoEnabled: videoEnabled !== false,
    };
  }, [audioEnabled, videoEnabled]);

  useEffect(() => {
    recordingStateRef.current = Boolean(recording);
  }, [recording]);

  useEffect(() => {
    if (!socket) return undefined;

    const isCurrentRoom = (payload = {}) =>
      !payload.roomId || String(payload.roomId) === String(roomId);
    const observerJoined = (payload = {}) => {
      if (!isCurrentRoom(payload) || !payload.observer) return;
      const observer = payload.observer;
      const observerKey = String(observer.auditId || observer.userId || '');
      setVisibleMeetingObservers((previous) => [
        ...previous.filter(
          (item) =>
            String(item.auditId || item.userId || '') !== observerKey
        ),
        observer,
      ]);
      // toast.info(
      //   payload.message ||
      //     `${observer.displayName || observer.role || 'Admin/Staff'} joined as a visible read-only observer.`,
      //   { toastId: `meeting-observer-${observerKey || 'joined'}` }
      // );
    };
    const observerLeft = (payload = {}) => {
      if (!isCurrentRoom(payload) || !payload.observer) return;
      const observerKey = String(
        payload.observer.auditId || payload.observer.userId || ''
      );
      setVisibleMeetingObservers((previous) =>
        previous.filter(
          (item) =>
            String(item.auditId || item.userId || '') !== observerKey
        )
      );
    };

    socket.on('meeting-observer-joined', observerJoined);
    socket.on('meeting-observer-left', observerLeft);
    return () => {
      socket.off('meeting-observer-joined', observerJoined);
      socket.off('meeting-observer-left', observerLeft);
    };
  }, [roomId, socket]);
  const transportStatsRef = useRef({
    send: { bitrate: 0, packetsSent: 0 },
    recv: { bitrate: 0, packetsReceived: 0 }
  });

  const syncMediaStateToServer = useCallback(({
    force = false,
    previousByKind = {},
  } = {}) => {
    const activeSocket = socketRef.current;
    const status = String(
      store.getState().room?.localParticipantStatus ||
        sessionStorage.getItem('statusget') ||
        ''
    );
    if (
      !activeSocket?.connected ||
      !['admitted', 'presenting'].includes(status)
    ) {
      return;
    }

    const attempted = { ...localMediaStateRef.current };
    activeSocket.timeout(5000).emit(
      'sync-media-state',
      {
        roomId,
        userId: userIdGet,
        sessionId,
        audioEnabled: attempted.audio === true,
        videoEnabled: attempted.video === true,
      },
      (error, response = {}) => {
        if (!error && response?.success !== false) {
          const version = Number(response?.state?.mediaStateVersion || 0);
          if (version > 0) {
            const currentVersion = Number(
              mediaStateVersionRef.current.get(String(sessionId)) || 0
            );
            if (version > currentVersion) {
              mediaStateVersionRef.current.set(String(sessionId), version);
            }
          }
          return;
        }

        console.warn(
          'Unable to persist local media state:',
          response.error || response.message || error?.message
        );
        toast.warning(
          'Microphone/camera status could not be synced. Restoring the previous setting.',
          { toastId: `media-state-sync-${roomId}`, autoClose: 3000 }
        );
        if (force) return;

        for (const kind of ['audio', 'video']) {
          const previous = previousByKind[kind];
          if (
            typeof previous !== 'boolean' ||
            localMediaStateRef.current[kind] !== attempted[kind]
          ) {
            continue;
          }
          suppressNextMediaSyncRef.current.add(kind);
          const rollback =
            kind === 'audio'
              ? mediaSoupHandler.current?.toggleAudio?.(previous)
              : mediaSoupHandler.current?.toggleVideo?.(previous);
          void Promise.resolve(rollback).catch((rollbackError) => {
            suppressNextMediaSyncRef.current.delete(kind);
            console.warn(`Unable to restore ${kind}:`, rollbackError);
          });
        }
      }
    );
  }, [roomId, sessionId, userIdGet]);

  const commitLocalMediaState = useCallback((state = {}, { force = false } = {}) => {
    const kind = String(state.kind || '').toLowerCase();
    if (!['audio', 'video'].includes(kind)) return;
    const enabled = state.enabled === true;
    const previous = localMediaStateRef.current[kind];
    localMediaStateRef.current = {
      ...localMediaStateRef.current,
      [kind]: enabled,
    };

    if (force || previous !== enabled) {
      dispatch(kind === 'audio' ? toggleAudio(enabled) : toggleVideo(enabled));
    }

    if (suppressNextMediaSyncRef.current.has(kind)) {
      suppressNextMediaSyncRef.current.delete(kind);
      return;
    }
    if (!force && previous === enabled) return;
    syncMediaStateToServer({
      force,
      previousByKind: { [kind]: previous },
    });
  }, [dispatch, syncMediaStateToServer]);

  const syncLocalMediaState = useCallback((handler, force = true) => {
    if (!handler?.getLocalMediaState) return;
    const audio = handler.getLocalMediaState('audio');
    const video = handler.getLocalMediaState('video');
    localMediaStateRef.current = {
      audio: audio.enabled === true,
      video: video.enabled === true,
    };
    dispatch(toggleAudio(audio.enabled === true));
    dispatch(toggleVideo(video.enabled === true));
    syncMediaStateToServer({ force });
  }, [dispatch, syncMediaStateToServer]);

  const announceRemoteScreenProducer = useCallback((remoteSessionId, producerId) => {
    const key = String(remoteSessionId || '');
    const cleanProducerId = String(producerId || '');
    if (!key || !cleanProducerId) return;

    const current = remoteScreenStateRef.current.get(key);
    if (current?.producerId === cleanProducerId) return;

    // Do not clear the old visible track while the replacement consumer is
    // being created.  The new track is swapped in atomically after consume().
    remoteScreenStateRef.current.set(key, {
      producerId: cleanProducerId,
      trackId: '',
    });
  }, []);

  const commitRemoteScreen = useCallback((remoteSessionId, producerId, track) => {
    const key = String(remoteSessionId || '');
    const cleanProducerId = String(producerId || '');
    if (!key || !track || track.readyState === 'ended') return false;

    const current = remoteScreenStateRef.current.get(key);
    // A slower consume for a replaced producer must never overwrite the newer
    // screen or make the UI oscillate between two tracks.
    if (
      cleanProducerId &&
      current?.producerId &&
      current.producerId !== cleanProducerId
    ) {
      return false;
    }

    const resolvedProducerId = cleanProducerId || current?.producerId || '';
    if (current?.producerId === resolvedProducerId && current?.trackId === track.id) {
      return false;
    }

    remoteScreenStateRef.current.set(key, {
      producerId: resolvedProducerId,
      trackId: track.id,
    });

    setRemoteScreens((previous) =>
      previous[key] === track ? previous : { ...previous, [key]: track }
    );
    dispatch(addRemoteScreen({ sessionId: key, userId: key, track }));
    return true;
  }, [dispatch]);

  const clearRemoteScreen = useCallback((remoteSessionId, producerId = '') => {
    const key = String(remoteSessionId || '');
    const cleanProducerId = String(producerId || '');
    if (!key) return false;

    const current = remoteScreenStateRef.current.get(key);
    // Ignore a late producerClosed event for the screen that was replaced.
    if (
      cleanProducerId &&
      current?.producerId &&
      current.producerId !== cleanProducerId
    ) {
      return false;
    }

    remoteScreenStateRef.current.delete(key);
    setRemoteScreens((previous) => {
      if (!Object.prototype.hasOwnProperty.call(previous, key)) return previous;
      const next = { ...previous };
      delete next[key];
      return next;
    });
    dispatch(removeRemoteScreen({ sessionId: key, userId: key }));
    return true;
  }, [dispatch]);

  const getRemoteProducerKey = useCallback(
    (remoteSessionId, mediaType) =>
      `${String(remoteSessionId || '').trim()}:${String(
        mediaType || ''
      ).toLowerCase()}`,
    []
  );

  const announceRemoteMediaProducer = useCallback((
    remoteSessionId,
    mediaType,
    producerId
  ) => {
    const cleanMediaType = String(mediaType || '').toLowerCase();
    const cleanProducerId = String(producerId || '').trim();
    if (
      !remoteSessionId ||
      !cleanProducerId ||
      !['audio', 'video'].includes(cleanMediaType)
    ) {
      return;
    }
    remoteProducerStateRef.current.set(
      getRemoteProducerKey(remoteSessionId, cleanMediaType),
      cleanProducerId
    );
  }, [getRemoteProducerKey]);

  const consumeRemoteProducer = useCallback(async (producer = {}) => {
    const producerId = String(producer.producerId || '');
    const remoteSessionId = String(producer.sessionId || '');
    if (!producerId || !remoteSessionId || remoteSessionId === String(sessionId)) return null;
    const handler = mediaSoupHandler.current;
    if (!handler) return null;
    const replacesProducerIds = [
      ...new Set(
        (Array.isArray(producer.replacesProducerIds)
          ? producer.replacesProducerIds
          : [])
          .map((value) => String(value || '').trim())
          .filter((value) => value && value !== producerId)
      ),
    ];

    const announcedMediaType = String(
      producer.mediaType || producer.appData?.type || producer.kind || ''
    ).toLowerCase();
    const announcedAsScreen = announcedMediaType === 'screen';
    const announcedKind =
      announcedMediaType === 'audio' ? 'audio' : 'video';
    const producerKey = announcedAsScreen
      ? ''
      : getRemoteProducerKey(remoteSessionId, announcedKind);
    const previousProducerId = producerKey
      ? remoteProducerStateRef.current.get(producerKey)
      : '';
    const previousScreenState = announcedAsScreen
      ? remoteScreenStateRef.current.get(remoteSessionId)
      : null;

    handler.producerSessionMap.set(producerId, remoteSessionId);
    if (announcedAsScreen) {
      announceRemoteScreenProducer(remoteSessionId, producerId);
    } else {
      announceRemoteMediaProducer(
        remoteSessionId,
        announcedKind,
        producerId
      );
    }

    const rollbackPendingAnnouncement = () => {
      if (announcedAsScreen) {
        const current = remoteScreenStateRef.current.get(remoteSessionId);
        if (current?.producerId !== producerId) return;
        if (previousScreenState) {
          remoteScreenStateRef.current.set(
            remoteSessionId,
            previousScreenState
          );
        } else {
          remoteScreenStateRef.current.delete(remoteSessionId);
        }
        return;
      }
      if (remoteProducerStateRef.current.get(producerKey) !== producerId) {
        return;
      }
      if (previousProducerId) {
        remoteProducerStateRef.current.set(
          producerKey,
          previousProducerId
        );
      } else {
        remoteProducerStateRef.current.delete(producerKey);
      }
    };

    let consumer;
    try {
      consumer = await handler.consume(producerId);
    } catch (error) {
      rollbackPendingAnnouncement();
      throw error;
    }
    if (!consumer?.track) {
      rollbackPendingAnnouncement();
      return null;
    }
    const mediaType = String(
      producer.mediaType || producer.appData?.type ||
        consumer.appData?.mediaType || consumer.appData?.type ||
      producer.kind || consumer.kind
    ).toLowerCase();

    if (mediaType === 'screen') {
      // Some old producer payloads called a screen track "video".  Only claim
      // the session here when no newer screen producer was announced meanwhile.
      const currentScreen = remoteScreenStateRef.current.get(remoteSessionId);
      if (!announcedAsScreen && !currentScreen?.producerId) {
        announceRemoteScreenProducer(remoteSessionId, producerId);
      }
      const committed = commitRemoteScreen(
        remoteSessionId,
        producerId,
        consumer.track
      );
      const currentAfterCommit =
        remoteScreenStateRef.current.get(remoteSessionId);
      if (
        !committed &&
        currentAfterCommit?.producerId &&
        currentAfterCommit.producerId !== producerId
      ) {
        await handler.closeConsumerByProducer(producerId);
        return null;
      }
    } else {
      const mediaKind = mediaType === 'audio' ? 'audio' : 'video';
      const resolvedProducerKey = getRemoteProducerKey(
        remoteSessionId,
        mediaKind
      );
      const currentProducerId =
        remoteProducerStateRef.current.get(resolvedProducerKey);
      // A replacement producer may become current while the old consume REST
      // call is still negotiating. Never let that late track overwrite it.
      if (
        currentProducerId &&
        currentProducerId !== producerId
      ) {
        await handler.closeConsumerByProducer(producerId);
        return null;
      }
      remoteProducerStateRef.current.set(resolvedProducerKey, producerId);
      setRemoteStreamState((previous) => {
        const next = { ...previous };
        const entry = next[remoteSessionId] || {
          stream: new MediaStream(),
          audio: null,
          video: null,
          screen: null,
        };
        // Producer replacement/recovery creates a new MediaStreamTrack. Keep
        // exactly one live track per media kind; retaining the ended camera or
        // microphone track makes Chromium continue rendering a frozen tile.
        const retainedTracks = (entry.stream?.getTracks?.() || []).filter(
          (track) =>
            track.kind !== consumer.track.kind && track.readyState === 'live'
        );
        const stream = new MediaStream([...retainedTracks, consumer.track]);
        next[remoteSessionId] = {
          ...entry,
          stream,
          [mediaKind]: consumer.track,
          producerIds: {
            ...(entry.producerIds || {}),
            [mediaKind]: producerId,
          },
        };
        return next;
      });
    }

    // The backend announces a ready replacement before retiring its old
    // producer. Keep the old consumer visible/audible until the new track has
    // committed above, then retire it without a blank or silent gap.
    await Promise.allSettled(
      replacesProducerIds.map(async (replacedProducerId) => {
        handler.markProducerStale?.(replacedProducerId);
        await handler.closeConsumerByProducer(replacedProducerId);
      })
    );
    return consumer;
  }, [
    announceRemoteScreenProducer,
    announceRemoteMediaProducer,
    commitRemoteScreen,
    getRemoteProducerKey,
    sessionId,
  ]);
  // ⬇️ add this
const launchGuardRef = useRef(true);

useEffect(() => installMeetingAudioUnlock(), []);
useEffect(() => {
  const container = meetingContainerRef.current;
  const controls = controlsBarRef.current;
  if (!container || !controls) return undefined;

  const updateControlClearance = () => {
    const height = Math.ceil(controls.getBoundingClientRect().height);
    if (height > 0) {
      container.style.setProperty('--meeting-controls-height', `${height}px`);
    }
  };

  updateControlClearance();
  const observer = typeof ResizeObserver === 'function'
    ? new ResizeObserver(updateControlClearance)
    : null;
  observer?.observe(controls);
  window.addEventListener('resize', updateControlClearance);
  window.addEventListener('orientationchange', updateControlClearance);

  return () => {
    observer?.disconnect();
    window.removeEventListener('resize', updateControlClearance);
    window.removeEventListener('orientationchange', updateControlClearance);
    container.style.removeProperty('--meeting-controls-height');
  };
}, []);
useEffect(() => {
  if (!socket) return;

  const handleMeetingEnded = async ({
    roomId: endedRoomId,
    endedBy,
    reason,
  } = {}) => {
    if (endedRoomId !== currentRoom?.roomId) return;

    try { await flushClassBoards(endedRoomId, { finalFlush: true }); }
    catch { toast.error('Some board changes could not be saved. Reopen the board in this browser to check for a recovery copy.'); }

    // The browser that owns the capture must flush its last chunk before the
    // page is closed. Other participants simply continue with cleanup.
    try {
      notifyRecordingStop(endedRoomId);
      await meetingRecorder.stopIfActive(endedRoomId);
    } catch (error) {
      console.error('Final recording flush failed:', error);
      toast.error(recordingUiError(error));
    }
    
    // 1. Media and Transport cleanup
    try {
      mediaSoupHandler.current?.destroy();
      mediaSoupHandler.current = null;
    } catch {}

    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      dispatch(clearMedia());
    }

    // 2. Storage Cleanup (ரிமோட் யூசர்களுக்கும் இது நடக்கும்)
    clearMeetingRuntimeStorage({ keepAuth: true });
    releaseRoomLock();

    // 3. UI and Navigation
    dispatch(clearRoom());
    dispatch(setLocalParticipantStatus(null));
    
    const endedAutomatically =
      String(endedBy || '').toLowerCase() === 'system' ||
      String(reason || '').toLowerCase() === 'scheduled-end-reconciliation';
    toast.info(
      endedAutomatically
        ? 'Scheduled meeting time ended automatically.'
        : 'Meeting ended by host',
      { autoClose: 5000 }
    );
    
    // Do not close the capture tab while its durable recording still needs the
    // app's recovery worker. An ended room still authorizes its original owner.
    const pending = await pendingDirectRecordings(endedRoomId, { includeActive: true }).catch(() => [true]);
    if (pending.length) navigate('/meeting-ended', { replace: true, state: { roomId: endedRoomId } });
    else closeOrReturnToDashboard(user);
  };

  socket.on('meeting-ended', handleMeetingEnded);
  
  return () => {
    socket.off('meeting-ended', handleMeetingEnded);
  };
}, [socket, currentRoom?.roomId, dispatch, localStream, user, navigate]);

useEffect(() => {
  if (!socket) return undefined;
  const handleAccessRevoked = async (payload = {}) => {
    if (payload.userId && String(payload.userId) !== String(userIdGet)) return;
    try { await mediaSoupHandler.current?.stopScreenSharing?.(); } catch {}
    try { mediaSoupHandler.current?.destroy(); } catch {}
    dispatch(clearMedia());
    clearMeetingRuntimeStorage({ keepAuth: true });
    try { releaseRoomLock(); } catch {}
    toast.error(
      payload.message ||
        (payload.permanent
          ? 'You have been permanently removed from this classroom.'
          : 'You were removed with a warning. You may request to join again.'),
      { autoClose: 6000 }
    );
    // Leave media immediately, but allow the removal voice/visible notice to
    // finish before navigation destroys the page's speech synthesis context.
    window.setTimeout(() => closeOrReturnToDashboard(user),
      payload.reason === 'classroom-discipline' ? 9000 : 900);
  };
  socket.on('meeting-access-revoked', handleAccessRevoked);
  socket.on('meeting-session-replaced', handleAccessRevoked);
  return () => {
    socket.off('meeting-access-revoked', handleAccessRevoked);
    socket.off('meeting-session-replaced', handleAccessRevoked);
  };
}, [dispatch, socket, user, userIdGet]);

useEffect(() => {
  const endAt = getPeriodEndTimestamp(currentRoom);
  if (!endAt) {
    setPeriodRemaining(null);
    return undefined;
  }
  const tick = () => {
    const remaining = Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
    setPeriodRemaining(remaining);
  };
  tick();
  const timer = window.setInterval(tick, 1000);
  return () => {
    window.clearInterval(timer);
  };
}, [currentRoom]);

// Schedule against the authoritative room end timestamp instead of counting
// down intervals, which drift while a background tab is throttled. The alert
// is persisted per room + scheduled end, so a remount/background-tab wake-up
// cannot display duplicate three-minute dialogs. A real extension creates a
// new authoritative end key and therefore gets one warning for the new end.
useEffect(() => {
  const endAt = getPeriodEndTimestamp(currentRoom);
  const activeRoomId = String(currentRoom?.roomId || roomId || '');
  if (!endAt || !activeRoomId || endAt <= Date.now()) {
    setThreeMinuteWarning(null);
    return undefined;
  }

  const alertKey = getMeetingEndWarningKey(activeRoomId, endAt);
  const showThreeMinuteAlert = () => {
    const remainingSeconds = Math.ceil((endAt - Date.now()) / 1000);
    const wasPersisted = readMeetingEndWarning(alertKey);
    if (!shouldShowThreeMinuteWarning({
      remainingSeconds,
      alertKey,
      lastAlertKey: threeMinuteAlertKeyRef.current,
      wasPersisted,
    })) return;
    threeMinuteAlertKeyRef.current = alertKey;
    persistMeetingEndWarning(alertKey);
    setThreeMinuteWarning({ key: alertKey, endAt });
  };

  setThreeMinuteWarning((current) => current?.endAt === endAt ? current : null);
  let timer = null;
  const scheduleAlert = () => {
    const delay = endAt - Date.now() - 3 * 60 * 1000;
    if (delay <= 0) {
      showThreeMinuteAlert();
      return;
    }
    timer = window.setTimeout(
      scheduleAlert,
      Math.min(delay, 2_147_000_000)
    );
  };
  scheduleAlert();
  return () => {
    if (timer) window.clearTimeout(timer);
  };
}, [
  currentRoom?.roomId,
  currentRoom?.scheduledEndAt,
  currentRoom?.endAt,
  currentRoom?.periodEndTime,
  currentRoom?.periodStartTime,
  currentRoom?.endTime,
  currentRoom?.startTime,
  currentRoom?.timetableMetadata?.endTime24,
  currentRoom?.timetableMetadata?.endTime,
  currentRoom?.timetableMetadata?.startTime24,
  currentRoom?.timetableMetadata?.startTime,
  currentRoom?.scheduledAt,
  currentRoom?.generatedAt,
  roomId,
]);

useEffect(() => {
  if (!socket || !currentRoom?.roomId || !['admitted', 'presenting'].includes(String(participantStatus))) {
    return undefined;
  }
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const sendHeartbeat = () => socket.emit('attendance:heartbeat', {
    sample: {
      online: navigator.onLine,
      effectiveType: connection?.effectiveType || '',
      downlink: connection?.downlink,
      rtt: connection?.rtt,
      bitrate: Number(
        transportStatsRef.current?.recv?.bitrate ||
          transportStatsRef.current?.send?.bitrate ||
          0
      ),
      quality: navigator.onLine ? 'connected' : 'offline',
      visibilityState: document.visibilityState,
    },
  });
  const incident = (type, code, message, recoverable = true) => socket.emit('attendance:incident', {
    incident: { type, code, message: String(message || '').slice(0, 500), recoverable },
  });
  const online = () => { incident('network', 'ONLINE', 'Connection restored'); sendHeartbeat(); };
  const offline = () => incident('network', 'OFFLINE', 'Browser went offline');
  const error = (event) => incident('client-error', 'WINDOW_ERROR', event.message || 'Client error');
  const rejection = (event) => incident('client-error', 'UNHANDLED_REJECTION', event.reason?.message || 'Unhandled client request error');
  const reconnect = () => { incident('socket', 'RECONNECTED', 'Socket connection restored'); sendHeartbeat(); };
  const reconnectError = (value) => incident('socket', 'RECONNECT_ERROR', value?.message || 'Socket reconnect failed');
  sendHeartbeat();
  const timer = window.setInterval(sendHeartbeat, 15000);
  window.addEventListener('online', online);
  window.addEventListener('offline', offline);
  window.addEventListener('error', error);
  window.addEventListener('unhandledrejection', rejection);
  document.addEventListener('visibilitychange', sendHeartbeat);
  socket.io?.on('reconnect', reconnect);
  socket.io?.on('reconnect_error', reconnectError);
  return () => {
    window.clearInterval(timer);
    window.removeEventListener('online', online);
    window.removeEventListener('offline', offline);
    window.removeEventListener('error', error);
    window.removeEventListener('unhandledrejection', rejection);
    document.removeEventListener('visibilitychange', sendHeartbeat);
    socket.io?.off('reconnect', reconnect);
    socket.io?.off('reconnect_error', reconnectError);
  };
}, [socket, currentRoom?.roomId, participantStatus]);
useEffect(() => {
  // ignore visibility events fired during initial app launch
  const t = setTimeout(() => { launchGuardRef.current = false; }, 1500);
  return () => clearTimeout(t);
}, []);

const openMeetingChat = useCallback((incomingMessage = null) => {
  setBlackboardMinimizeRequestId((value) => value + 1);
  setShowParticipants(false);
  setShowMeetingInfo(false);
  setShowBible(false);
  setShowChat(true);
  dispatch(toggleChatStatus(true));
  setRequestedChatTarget({
    isPrivate: incomingMessage?.isPrivate === true,
    sessionId: incomingMessage?.senderSessionId || '',
    userId: incomingMessage?.userId || '',
    username: incomingMessage?.username || 'Participant',
    requestId: `${incomingMessage?._id || incomingMessage?.id || 'global'}-${Date.now()}`,
  });
}, [dispatch]);

const refreshUnreadCount = useCallback(async () => {
  if (!roomId) return;
  const requestSequence = ++unreadRefreshSequenceRef.current;
  try {
    const { data } = await api.get(`/rooms/${roomId}/chat/unread`, {
      params: { _ts: Date.now() },
    });
    if (requestSequence !== unreadRefreshSequenceRef.current) return;
    const nextUnread = Number(data?.unread) || 0;
    setUnreadMessages(nextUnread);
    setLastIncomingChat(nextUnread > 0 ? (data?.latestUnread || null) : null);
  } catch {}
}, [roomId]);

useEffect(() => {
  if (!currentRoom?.roomId) return;

  void refreshUnreadCount();

  // pending requests from participants in store
  const waiting = (currentRoom.participants || []).filter(p => p.status === 'waiting').length;
  setPendingRequests(waiting);
}, [currentRoom?.roomId, refreshUnreadCount]);
useEffect(() => {
  const waiting = (currentRoom?.participants || []).filter(p => p.status === 'waiting').length;
  setPendingRequests(waiting);
}, [currentRoom?.participants]);

//  useEffect(() => {
//     if (isNative()) return; // native handled by MainActivity
//     const onHide = async () => {
//       if (document.visibilityState === 'hidden') {
//         await enterPiPFor(mainVideoRef.current);
//       }
//     };
//     document.addEventListener('visibilitychange', onHide);
//     return () => document.removeEventListener('visibilitychange', onHide);
//   }, []);
// 3) Recipient-only visible alert + authoritative unread reconciliation.
useEffect(() => {
  if (!socket) return;
  const currentUserId = String(
    user?.id || user?._id || sessionStorage.getItem('userId') || ''
  );

  const onIncomingMessage = (msg) => {
    if (msg?.roomId && String(msg.roomId) !== String(roomId)) return;
    if (String(msg?.userId || '') === currentUserId) return;
    if (
      msg?.isPrivate === true &&
      String(msg?.recipientUserId || '') !== currentUserId
    ) return;

    setLastIncomingChat(msg);
    const notificationText = msg?.isPrivate
      ? `New private message from ${msg?.username || 'Participant'}`
      : `New meeting message from ${msg?.username || 'Participant'}`;
    toast.info(notificationText, {
      toastId: `meeting-chat-${msg?._id || msg?.id || msg?.timestamp || notificationText}`,
      autoClose: 7000,
      onClick: () => openMeetingChat(msg),
    });

    // Capacitor notifications are supplemental only. A rejected web/native
    // promise must never prevent the in-meeting toast, badge or side alert.
    if (!showChat || document.visibilityState !== 'visible') {
      void onChatMessage(msg).catch(() => undefined);
    }
    void refreshUnreadCount();
  };

  socket.on('newMessage', onIncomingMessage);
  socket.on('newPrivateMessage', onIncomingMessage);
  return () => {
    socket.off('newMessage', onIncomingMessage);
    socket.off('newPrivateMessage', onIncomingMessage);
  };
}, [
  openMeetingChat,
  refreshUnreadCount,
  roomId,
  showChat,
  socket,
  user?.id,
  user?._id,
]);

useEffect(() => {
  const refresh = () => { void refreshUnreadCount(); };
  window.addEventListener('meeting-chat-unread-changed', refresh);
  return () => window.removeEventListener('meeting-chat-unread-changed', refresh);
}, [refreshUnreadCount]);

// Chat.jsx advances the server watermark only to its latest visible message.
// Keep the controls slice aligned with the actual panel opened from the top
// badge, bottom controls, toast or side alert.
useEffect(() => {
  dispatch(toggleChatStatus(showChat));
}, [dispatch, showChat]);

// 5) Plug into your existing toggler

  // Stable meeting identity and stale-safe single-tab room lock.
  const deviceId = getOrCreateMeetingDeviceId();
  const deviceSig =
    sessionStorage.getItem('deviceSig') ||
    localStorage.getItem('deviceSig') ||
    deviceId;

  sessionStorage.setItem('deviceId', deviceId);
  sessionStorage.setItem('deviceSig', deviceSig);
  localStorage.setItem('deviceId', deviceId);
  localStorage.setItem('deviceSig', deviceSig);

  const roomLockKey = `meet-lock:${roomId}:${deviceSig}`;
  const roomLockTtlMs = 90_000;
  const bc = useRef(null);
  const iOwnLockRef = useRef(false);

  const readRoomLock = useCallback(() => {
    try {
      const raw = localStorage.getItem(roomLockKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [roomLockKey]);

  const writeRoomLock = useCallback(() => {
    localStorage.setItem(
      roomLockKey,
      JSON.stringify({ ts: Date.now(), pid: sessionId })
    );
  }, [roomLockKey, sessionId]);

  const acquireRoomLock = useCallback(() => {
    try {
      const existing = readRoomLock();
      const existingIsFresh =
        existing?.ts && Date.now() - Number(existing.ts) < roomLockTtlMs;
      const belongsToCurrentSession = existing?.pid === sessionId;

      if (existingIsFresh && !belongsToCurrentSession) {
        return false;
      }

      writeRoomLock();
      iOwnLockRef.current = true;
      bc.current?.postMessage({
        type: 'lock-claimed',
        roomId,
        deviceId,
        sessionId,
      });
      return true;
    } catch {
      // localStorage can be unavailable in strict privacy modes. Do not block
      // the user when the browser cannot maintain the lock.
      iOwnLockRef.current = true;
      return true;
    }
  }, [deviceId, readRoomLock, roomId, sessionId, writeRoomLock]);

  const releaseRoomLock = useCallback(() => {
    try {
      const existing = readRoomLock();
      if (!existing || existing.pid === sessionId) {
        localStorage.removeItem(roomLockKey);
      }

      bc.current?.postMessage({
        type: 'lock-released',
        roomId,
        deviceId,
        sessionId,
      });
    } catch {
      // Ignore storage cleanup errors.
    } finally {
      iOwnLockRef.current = false;
    }
  }, [deviceId, readRoomLock, roomId, roomLockKey, sessionId]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!iOwnLockRef.current) return;

      try {
        const existing = readRoomLock();
        if (existing?.pid === sessionId) writeRoomLock();
      } catch {
        // Ignore heartbeat storage errors.
      }
    }, 30_000);

    return () => window.clearInterval(timer);
  }, [readRoomLock, sessionId, writeRoomLock]);

  useEffect(() => {
    if ('BroadcastChannel' in window) {
      bc.current = new BroadcastChannel(`meet:${roomId}:${deviceId}`);
      bc.current.onmessage = (event) => {
        const message = event.data || {};
        if (
          message.type === 'lock-claimed' &&
          message.sessionId !== sessionId &&
          !iOwnLockRef.current
        ) {
          toast.error(
            "You're already in this meeting in another tab on this device."
          );
          navigate('/join-room?blocked=1', { replace: true });
        }
      };
    }

    const onStorage = (event) => {
      if (event.key !== roomLockKey || !event.newValue || iOwnLockRef.current) {
        return;
      }

      try {
        const lock = JSON.parse(event.newValue);
        if (lock?.pid && lock.pid !== sessionId) {
          toast.error(
            "You're already in this meeting in another tab on this device."
          );
          navigate('/join-room?blocked=1', { replace: true });
        }
      } catch {
        // Ignore malformed storage events.
      }
    };

    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
      try {
        bc.current?.close();
      } catch {
        // Ignore BroadcastChannel close errors.
      }
      bc.current = null;
    };
  }, [deviceId, navigate, roomId, roomLockKey, sessionId]);

  useEffect(() => {
    const onUnload = () => releaseRoomLock();
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [releaseRoomLock]);

  const memoizedRemoteVideoStatus = useMemo(() => remoteVideoStatus, [remoteVideoStatus]);
const lastRoomData = useRef(null);
const fetchRoomFromAPI = useCallback(async () => {
  const response = await api.get(`/rooms/${roomId}`);
  sessionStorage.setItem(
    'participantsGet',
    JSON.stringify(response.data.participants || [])
  );
  sessionStorage.setItem('hostGet', String(response.data.host?._id || response.data.host || ''));
  sessionStorage.setItem(
    'originalHost',
    String(response.data.originalHost?._id || response.data.originalHost || response.data.host?._id || response.data.host || '')
  );
  dispatch(setRoom(response.data));
  return response.data;
}, [dispatch, roomId]);
useEffect(() => {
  if (!socket) return;

  const handleHostTransferred = ({ newHostId, newHostUsername }) => {
    const myUserId = String(userIdGet || '');

    if (myUserId === String(newHostId || '')) {
      sessionStorage.setItem('isHost', 'true');
      dispatch(setLocalParticipantStatus({ isHost: true }));
      toast.info('You are now the host');
    } else {
      sessionStorage.setItem('isHost', 'false');
      dispatch(setLocalParticipantStatus({ isHost: false }));
    }

    dispatch(updateParticipant({ hostId: newHostId }));
    const latestRoom = store.getState().room.currentRoom || {};
    dispatch(setRoom({
      ...latestRoom,
      host: { _id: newHostId, username: newHostUsername }
    }));
  };

  socket.on('host-transferred', handleHostTransferred);

  return () => {
    socket.off('host-transferred', handleHostTransferred);
  };
}, [dispatch, socket, userIdGet]);


 const initMeeting = useCallback(async () => {
      if (initStartedRef.current) return;
      initStartedRef.current = true;

      try {
        if (!meetingIdentity.valid || !sessionId || !userIdGet) {
          throw new Error(
            'Your meeting login session is incomplete. Please open the meeting link again.'
          );
        }
        // 🔒 Same-device, same-room single join guard
        const ok = acquireRoomLock();
        if (!ok) {
          initStartedRef.current = false;
         dispatch(
        showAlert({
          severity: 'warning',
          message:
            "You're already in this meeting on this device. Only one session is allowed per device. Close this tab or join from another device.",
        })
      );
          navigate('/', { replace: true });
          return;
        }

        sessionStorage.setItem('lastRoomId', roomId);
        

        // 🔁 Always fetch room data freshly
        const roomData = await fetchRoomFromAPI();
        const { rtpCapabilities, participants, host } = roomData;
        const initialPublishAccess = resolveInitialMeetingPublishAccess({
          room: roomData,
          userId: userIdGet,
          sessionId,
        });

        const device = new Device();
        await device.load({ routerRtpCapabilities: rtpCapabilities });

        const desiredMediaState = store.getState().media || {};
        desiredMediaStateRef.current = {
          audioEnabled: desiredMediaState.audioEnabled !== false,
          videoEnabled: desiredMediaState.videoEnabled !== false,
        };
        let stream = new MediaStream();
        let mediaCapturePrepared = false;
        let mediaPublishingStarted = false;
        const preparePublishingStream = async () => {
          if (mediaCapturePrepared) return stream;
          const desired = { ...desiredMediaStateRef.current };
          stream = await acquireMeetingMedia(desired);
          stream.getAudioTracks().forEach((track) => {
            track.enabled = desired.audioEnabled === true;
          });
          stream.getVideoTracks().forEach((track) => {
            track.enabled = desired.videoEnabled === true;
          });

          dispatch(setLocalStream(stream));
          const initialAudioEnabled = Boolean(
            stream.getAudioTracks().some(
              (track) => track.readyState === 'live' && track.enabled !== false
            )
          );
          const initialVideoEnabled = Boolean(
            stream.getVideoTracks().some(
              (track) => track.readyState === 'live' && track.enabled !== false
            )
          );
          localMediaStateRef.current = {
            audio: initialAudioEnabled,
            video: initialVideoEnabled,
          };
          dispatch(toggleAudio(initialAudioEnabled));
          dispatch(toggleVideo(initialVideoEnabled));
          mediaCapturePrepared = true;
          return stream;
        };

        if (initialPublishAccess.mayPublish) {
          await preparePublishingStream();
        } else {
          // Waiting-room privacy: retain the desired settings above, but do
          // not call getUserMedia until an authoritative socket admission.
          dispatch(setLocalStream(stream));
          localMediaStateRef.current = { audio: false, video: false };
        }

       const base = getMeetingSocketBaseUrl();

const newSocket = io(base, {
  path: '/socket.io',
  transports: ['websocket', 'polling'],
  tryAllTransports: true,
  withCredentials: true,
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 750,
  reconnectionDelayMax: 10000,
  randomizationFactor: 0.5,
  timeout: 20000,
  // Socket.IO calls this for every handshake, including reconnects after the
  // original short-lived JWT has been refreshed by REST.
  auth: (callback) => callback({ token: getStoredAccessToken() }),
});

        socketRef.current = newSocket;

        // Register before the initial joinRoom acknowledgement. Core sends the
        // current visible-observer presence immediately when a participant
        // joins, including when the observer was already watching beforehand.
        newSocket.on('meeting-observers-updated', (payload = {}) => {
          if (
            payload.roomId &&
            String(payload.roomId) !== String(roomId)
          ) {
            return;
          }
          if (Array.isArray(payload.observers)) {
            setVisibleMeetingObservers(payload.observers);
          }
        });

        // Register before joinRoom. The backend can replay already-saved
        // waiting requests as soon as an admin/assigned faculty joins.
        const handleIncomingJoinRequest = async ({ participant } = {}) => {
          if (!participant) return;

          void playJoinRequestSound(
            `join-request:${
              participant.sessionId ||
              participant._id ||
              participant.userId ||
              Date.now()
            }`
          );

          dispatch(addParticipant({
            participant,
            currentUserId: userIdGet,
            sessionId,
            socketId: newSocket.id,
          }));

          toast.info(
            `${getMeetingDisplayName(participant, 'A participant')} is waiting to join`,
            {
              toastId: `join-request-${
                participant.sessionId || participant._id || participant.userId
              }`,
            }
          );

          try {
            await fetchRoomFromAPI();
          } catch (refreshError) {
            console.warn('Waiting participant refresh failed:', refreshError);
          }
        };

        newSocket.on('join-request', handleIncomingJoinRequest);
        
        mediaSoupHandler.current = new MediaSoupHandler({
          socket: newSocket,
          device,
          roomId,
          userId: userIdGet,
          sessionId,
        });
        mediaSoupHandler.current.setSignalingReady(false);
        mediaSoupHandler.current.onLocalMediaStateChange = (state) => {
          commitLocalMediaState(state);
          if (state.reason === 'track-unavailable') {
            toast.warning(
              state.kind === 'video'
                ? 'Camera became unavailable and was turned off. Choose a camera, then turn it on again.'
                : 'Microphone became unavailable and was muted. Choose a microphone, then unmute.'
            );
          }
        };
        mediaSoupHandler.current.onRemoteMediaStateChange = ({
          producerId,
          sessionId: changedSessionId,
          mediaType,
          enabled,
        } = {}) => {
          const cleanSessionId = String(changedSessionId || '').trim();
          const cleanMediaType = String(mediaType || '').toLowerCase();
          if (!cleanSessionId || !['audio', 'video'].includes(cleanMediaType)) {
            return;
          }
          const currentProducerId = remoteProducerStateRef.current.get(
            getRemoteProducerKey(cleanSessionId, cleanMediaType)
          );
          // producerpause/producerresume from a just-replaced consumer can
          // arrive after the new producer is live. Never let that late event
          // flip the current participant's mic/camera badge.
          if (
            currentProducerId &&
            producerId &&
            currentProducerId !== String(producerId)
          ) {
            return;
          }
          if (cleanMediaType === 'video') {
            setRemoteVideoStatus((previous) => ({
              ...previous,
              [cleanSessionId]: Boolean(enabled),
            }));
          }
          dispatch(updateParticipant({
            participantId: cleanSessionId,
            updates: {
              [cleanMediaType === 'audio'
                ? 'audioEnabled'
                : 'videoEnabled']: Boolean(enabled),
            },
          }));
        };
        window.mediaSoupHandler = mediaSoupHandler.current;
        mediaSoupHandler.current.onTrackRecovered = (
          recoveredSessionId,
          kind,
          track,
          producerId
        ) => {
           if (!recoveredSessionId || !track) return;
           if (String(kind).toLowerCase() === 'screen') {
             commitRemoteScreen(recoveredSessionId, producerId, track);
             return;
           }
           const mediaKind =
             String(kind).toLowerCase() === 'audio' ? 'audio' : 'video';
           const producerKey = getRemoteProducerKey(
             recoveredSessionId,
             mediaKind
           );
           const currentProducerId =
             remoteProducerStateRef.current.get(producerKey);
           if (
             currentProducerId &&
             producerId &&
             currentProducerId !== String(producerId)
           ) {
             return;
           }
           if (producerId) {
             remoteProducerStateRef.current.set(
               producerKey,
               String(producerId)
             );
           }
           setRemoteStreamState(prev => {
              const newState = { ...prev };
              const entry = newState[recoveredSessionId] || {
                stream: new MediaStream(), audio: null, video: null, screen: null,
              };
              const retainedTracks = (entry.stream?.getTracks?.() || []).filter(
                (existingTrack) =>
                  existingTrack.kind !== track.kind &&
                  existingTrack.readyState === 'live'
              );
              newState[recoveredSessionId] = {
                ...entry,
                stream: new MediaStream([...retainedTracks, track]),
                [mediaKind]: track,
                producerIds: {
                  ...(entry.producerIds || {}),
                  [mediaKind]: String(producerId || ''),
                },
              };
              return newState;
           });
        };
        mediaSoupHandler.current.onConsumerTrackEnded = () => {
          // Producer replacement and mobile network hand-offs can end a
          // consumer track without disconnecting Socket.IO. Re-querying the
          // producer list restores the replacement automatically.
          window.setTimeout(() => void fetchAndConsumeRemoteStreams(), 250);
        };
        let initialJoinSettled = false;
        const duplicateSessionMessage =
          "You're already in this meeting on this device. Only one session is allowed per device. Close this tab or join from another device.";
        const joinPayload = () => ({
          roomId,
          userId: userIdGet,
          sessionId,
          statusGet: 'check',
          deviceId,
          rejoinStatus: sessionStorage.getItem('rejoinStatus'),
        });

        const createSocketJoinError = (response = {}) => {
          const error = new Error(
            response.error ||
              response.message ||
              'Unable to join the meeting.'
          );
          error.code = String(
            response.code || response.reason || response.error || ''
          ).trim();
          error.joinResponse = response;
          error.joinFailure = classifyMeetingJoinFailure(response);
          return error;
        };

        const stopMediaAfterFatalJoin = () => {
          mediaSoupHandler.current?.setSignalingReady?.(false);
          try { mediaSoupHandler.current?.destroy?.(); } catch {}
          stream?.getTracks?.().forEach((track) => {
            try {
              track.enabled = false;
              track.stop();
            } catch {}
          });
          mediaPublishingStarted = false;
          dispatch(clearMedia());
          releaseRoomLock();
          if (newSocket.connected) newSocket.disconnect();
        };

        const presentSocketJoinFailure = (error) => {
          const classification =
            error?.joinFailure || classifyMeetingJoinFailure(error);
          if (
            ![
              'session-invalid',
              'meeting-ended',
              'not-started',
              'access-denied',
            ].includes(classification)
          ) {
            return false;
          }

          stopMediaAfterFatalJoin();
          if (classification === 'meeting-ended') {
            sessionStorage.setItem('meetingEnd', 'end');
            toast.info(error?.message || 'This meeting has ended.', {
              toastId: `join-ended-${roomId}`,
            });
            navigate('/meeting-ended', {
              replace: true,
              state: { roomId },
            });
            return true;
          }
          if (classification === 'not-started') {
            toast.info(
              error?.message || 'This meeting is not open yet.',
              { toastId: `join-not-started-${roomId}` }
            );
            navigate(
              `/join-room?roomIdGet=${encodeURIComponent(roomId)}`,
              { replace: true }
            );
            return true;
          }
          if (classification === 'session-invalid') {
            toast.error(
              'Your meeting login session expired. Please sign in and join again.',
              { toastId: `join-session-invalid-${roomId}` }
            );
            const redirect = encodeURIComponent(
              `/join-room?roomIdGet=${encodeURIComponent(roomId)}`
            );
            navigate(`/login?redirect=${redirect}`, { replace: true });
            return true;
          }

          toast.error(
            error?.message || 'You no longer have access to this meeting.',
            { toastId: `join-access-denied-${roomId}` }
          );
          navigate('/', { replace: true });
          return true;
        };

        const applySocketJoin = async (response, { reconnect = false } = {}) => {
          if (
            response?.code === 'DUPLICATE_DEVICE_SESSION' ||
            response?.error === 'DUPLICATE_DEVICE_SESSION'
          ) {
            dispatch(
              showAlert({
                severity: 'warning',
                message: duplicateSessionMessage,
              })
            );
            navigate('/', { replace: true });
            return false;
          }
          if (response?.error) {
            throw createSocketJoinError(response);
          }

          const access = response?.access || {};
          const latestParticipants =
            store.getState().room.currentRoom?.participants || participants;
          const self = latestParticipants.find(
            (participant) =>
              (String(participant.user?._id || participant.user || '') ===
                String(userIdGet)) &&
              String(participant.sessionId || '') === String(sessionId)
          );
          const effectiveStatus =
            response?.status || self?.status || (access.isHost ? 'admitted' : 'waiting');
          const mayPublish =
            access.isHost === true ||
            ['admitted', 'presenting'].includes(effectiveStatus);

          persistRoomAccess({
            access,
            status: effectiveStatus,
            meetingType: response?.meetingType || 'meeting',
          });
          dispatch(setLocalParticipantStatus(effectiveStatus));

          if (response?.rtpCapabilities) {
            await mediaSoupHandler.current?.loadDevice(response.rtpCapabilities);
          }

          if (!mayPublish) {
            mediaSoupHandler.current?.setSignalingReady?.(false);
            if (mediaCapturePrepared && !mediaPublishingStarted) {
              stream.getTracks().forEach((track) => {
                try {
                  track.enabled = false;
                  track.stop();
                } catch {}
              });
              stream = new MediaStream();
              mediaCapturePrepared = false;
              dispatch(setLocalStream(stream));
              localMediaStateRef.current = { audio: false, video: false };
            }
            if (response?.joinRequestProcessed !== true) {
              newSocket.emit('request-join', {
                roomId,
                userId: userIdGet,
                sessionId,
                deviceId: deviceSig || deviceId,
              });
            }
            return true;
          }

          const publishingStream = await preparePublishingStream();
          mediaSoupHandler.current?.setSignalingReady?.(true);
          if (reconnect && mediaPublishingStarted) {
            await mediaSoupHandler.current?.recoverAfterSignalingReconnect({
              socket: newSocket,
              roomId,
              userId: userIdGet,
              sessionId,
            });
          } else {
            await mediaSoupHandler.current?.joinRoom({
              stream: publishingStream,
            });
            mediaPublishingStarted = true;
          }
          mediaSoupHandler.current?.startAdaptiveController?.();
          syncLocalMediaState(mediaSoupHandler.current, true);
          await fetchAndConsumeRemoteStreams();
          return true;
        };

        const recoverConnectedSocket = () => {
          if (!newSocket.connected) return Promise.resolve();
          if (socketRecoveryPromiseRef.current) {
            return socketRecoveryPromiseRef.current;
          }

          const recovery = (async () => {
            const response = await emitWithAckTimeout(
              newSocket,
              'joinRoom',
              joinPayload()
            );
            const joined = await applySocketJoin(response, { reconnect: true });
            if (!joined) return;

            try {
              const updatedRoom = await fetchRoomFromAPI();
              const self = (updatedRoom.participants || []).find(
                (participant) =>
                  (participant.user === userIdGet ||
                    participant.user?._id === userIdGet) &&
                  participant.sessionId === sessionId
              );
              if (self) dispatch(setLocalParticipantStatus(self.status));
            } catch (refreshError) {
              console.warn('Room refresh after reconnect failed:', refreshError);
            }

            toast.success('Connection restored', {
              toastId: `meeting-reconnected-${roomId}`,
              autoClose: 1800,
            });
          })().catch((error) => {
            if (presentSocketJoinFailure(error)) return false;
            throw error;
          });
          socketRecoveryPromiseRef.current = recovery;
          return recovery.finally(() => {
            if (socketRecoveryPromiseRef.current === recovery) {
              socketRecoveryPromiseRef.current = null;
            }
          });
        };
        mediaSoupHandler.current.onSignalingRejoinRequired = () =>
          recoverConnectedSocket();
        mediaSoupHandler.current.onAuthenticationRequired = async () => {
          const refreshed = await refreshAuthSession();
          return Boolean(refreshed?.token || getStoredAccessToken());
        };

        newSocket.on('disconnect', (reason) => {
          if (sessionStorage.getItem('meetingEnd') === 'end') return;
          mediaSoupHandler.current?.setSignalingReady?.(false);
          console.warn('Meeting socket disconnected:', reason);
          toast.info('Connection interrupted. Reconnecting...', {
            toastId: `meeting-reconnect-${roomId}`,
            autoClose: 2500,
          });
        });

        newSocket.on('connect_error', (error) => {
          mediaSoupHandler.current?.setSignalingReady?.(false);
          console.error('Meeting socket connection error:', error);

          if (
            /auth|jwt|token|unauthor/i.test(String(error?.message || '')) &&
            !socketAuthRefreshPromiseRef.current
          ) {
            const refresh = refreshAuthSession()
              .then(() => {
                if (!newSocket.connected) newSocket.connect();
              })
              .catch((refreshError) => {
                console.error('Meeting authentication refresh failed:', refreshError);
              })
              .finally(() => {
                if (socketAuthRefreshPromiseRef.current === refresh) {
                  socketAuthRefreshPromiseRef.current = null;
                }
              });
            socketAuthRefreshPromiseRef.current = refresh;
          }

          if (sessionStorage.getItem('meetingEnd') !== 'end') {
            toast.warning('Unable to connect. Retrying...', {
              toastId: `meeting-connect-error-${roomId}`,
              autoClose: 2500,
            });
          }
        });

        // The namespace `connect` event is authoritative. Manager `reconnect`
        // can fire before this namespace is ready, which previously sent
        // transport requests against a stale socket session.
        newSocket.on('connect', () => {
          if (!initialJoinSettled) return;
          void recoverConnectedSocket().catch((error) => {
            console.error('Reconnection failed:', error);
            toast.warning(
              'Connection restored; media recovery is retrying automatically.',
              { toastId: `meeting-media-recovery-${roomId}` }
            );
          });
        });

        newSocket.once('connect', () => {
          console.log('🔌 Socket connected:', newSocket.id);
          void (async () => {
            try {
              const response = await emitWithAckTimeout(
                newSocket,
                'joinRoom',
                joinPayload()
              );
              await applySocketJoin(response);
            } catch (error) {
              if (!presentSocketJoinFailure(error)) {
                toast.error(error?.message || 'Unable to join the meeting.');
                navigate(
                  `/join-room?roomIdGet=${encodeURIComponent(roomId)}`,
                  { replace: true }
                );
              }
            } finally {
              initialJoinSettled = true;
            }
          })();
// Personalised room updates carry backend-computed access flags.
const applyRoomUpdate = (payload) => {
  if (!payload?.roomId || payload.roomId !== roomId) return;

  for (const participant of payload.participants || []) {
    if (!['admitted', 'presenting'].includes(participant?.status)) continue;
    const activeSessionId = String(participant?.sessionId || '').trim();
    if (!activeSessionId) continue;

    for (const eventKey of [...participantExitEventsRef.current]) {
      if (eventKey.startsWith(`${activeSessionId}:`)) {
        participantExitEventsRef.current.delete(eventKey);
      }
    }
  }

  const previousRoom = store.getState().room.currentRoom || {};
  dispatch(setRoom({
    ...payload,
    meetingType: payload.meetingType || previousRoom.meetingType || 'meeting',
    access: payload.access || previousRoom.access || {}
  }));
};

newSocket.on('room-updated', applyRoomUpdate);
newSocket.on('room-refresh-required', async ({ roomId: changedRoomId } = {}) => {
  if (changedRoomId && changedRoomId !== roomId) return;
  try {
    const updatedRoom = await fetchRoomFromAPI();
    dispatch(setRoom(updatedRoom));
  } catch (error) {
    console.error('room-refresh-required error:', error);
  }
});

// In your socket connection setup
newSocket.on('participantUpdated', ({ participantId, updates }) => {
  const safeUpdates = { ...(updates || {}) };
  const cleanParticipantId = String(participantId || '').trim();
  const containsMediaState =
    typeof safeUpdates.audioEnabled === 'boolean' ||
    typeof safeUpdates.videoEnabled === 'boolean';
  if (containsMediaState && cleanParticipantId) {
    const incomingVersion = Number(safeUpdates.mediaStateVersion || 0);
    const currentVersion = Number(
      mediaStateVersionRef.current.get(cleanParticipantId) || 0
    );
    if (
      currentVersion > 0 &&
      (!incomingVersion || incomingVersion <= currentVersion)
    ) {
      delete safeUpdates.audioEnabled;
      delete safeUpdates.videoEnabled;
    } else if (incomingVersion > currentVersion) {
      mediaStateVersionRef.current.set(cleanParticipantId, incomingVersion);
    }
  }
  dispatch(updateParticipant({ participantId, updates: safeUpdates }));
  
  // Special handling for video status changes
  if (safeUpdates.videoEnabled !== undefined) {
    setRemoteVideoStatus(prev => ({
      ...prev,
      [participantId]: safeUpdates.videoEnabled
    }));
  }
});

          newSocket.on('participant-status-updated', async ({ status }) => {
            dispatch(setLocalParticipantStatus(status));
          
            if (status === 'admitted') {
              toast.success('You have been admitted to the meeting');
              sessionStorage.setItem('statusget', status);
              try {
                const response = await emitWithAckTimeout(
                  newSocket,
                  'joinRoom',
                  joinPayload()
                );
                await applySocketJoin(response);
                const updatedRoom = await fetchRoomFromAPI();
                dispatch(setRoom(updatedRoom));
              } catch (error) {
                if (!presentSocketJoinFailure(error)) {
                  toast.error(
                    error?.message || 'Unable to start meeting media.'
                  );
                }
              }
            } else if (status === 'rejected') {
              mediaSoupHandler.current?.setSignalingReady?.(false);
              toast.error('You have been rejected by the host');
            }
          });
          
          
          
          

          // join-request listener is registered before joinRoom above.

          newSocket.on('newParticipant', async (participant) => {
            const joinedSessionId = String(participant?.sessionId || '').trim();
            if (joinedSessionId) {
              for (const eventKey of [...participantExitEventsRef.current]) {
                if (eventKey.startsWith(`${joinedSessionId}:`)) {
                  participantExitEventsRef.current.delete(eventKey);
                }
              }
            }

            dispatch(addParticipant({
              participant,
              currentUserId: userIdGet,
              sessionId,
              socketId: newSocket.id,
            }));
          
            try {
              await fetchAndConsumeRemoteStreams();
            } catch (e) {
              console.warn("Error fetching producers:", e);
            }
          });
          
          

          newSocket.on('newProducer', async (producer) => {
            try {
              await consumeRemoteProducer(producer);
            } catch (err) {
              const classification = classifyMediaApiError(err);
              if (classification !== 'stale-producer') {
                console.warn('consume error:', err.message);
              }
              if (
                ['transient', 'authorization', 'stale-transport'].includes(
                  classification
                )
              ) {
                window.setTimeout(() => {
                  if (!newSocket.connected) return;
                  void consumeRemoteProducer(producer).catch((retryError) => {
                    if (
                      classifyMediaApiError(retryError) !== 'stale-producer'
                    ) {
                      console.warn(
                        'Replacement producer retry failed:',
                        retryError?.message || retryError
                      );
                    }
                  });
                }, 2000);
              }
            }
          });

          newSocket.on('producerClosed', ({
            producerId,
            sessionId: remoteSessionId,
            kind,
            actualKind,
          }) => {
            void mediaSoupHandler.current?.closeConsumerByProducer?.(producerId);
            if (String(kind) === 'screen' && remoteSessionId) {
              clearRemoteScreen(remoteSessionId, producerId);
              return;
            }
            if (remoteSessionId && ['audio', 'video'].includes(String(actualKind || kind))) {
              const mediaKind = String(actualKind || kind);
              const key = String(remoteSessionId);
              const producerKey = getRemoteProducerKey(key, mediaKind);
              const currentProducerId =
                remoteProducerStateRef.current.get(producerKey);

              mediaSoupHandler.current?.markProducerStale?.(producerId);
              // Transport recovery replaces a producer before the server's
              // close event for the old producer can arrive. Never erase that
              // replacement track with the late event.
              if (
                currentProducerId &&
                producerId &&
                currentProducerId !== String(producerId)
              ) {
                return;
              }
              remoteProducerStateRef.current.delete(producerKey);
              setRemoteStreamState((previous) => {
                const entry = previous[key];
                if (!entry) return previous;
                const entryProducerId = entry.producerIds?.[mediaKind];
                if (
                  entryProducerId &&
                  producerId &&
                  entryProducerId !== String(producerId)
                ) {
                  return previous;
                }
                const retainedTracks = (entry.stream?.getTracks?.() || []).filter(
                  (track) => track.kind !== mediaKind && track.readyState === 'live'
                );
                return {
                  ...previous,
                  [key]: {
                    ...entry,
                    stream: new MediaStream(retainedTracks),
                    [mediaKind]: null,
                    producerIds: {
                      ...(entry.producerIds || {}),
                      [mediaKind]: '',
                    },
                  },
                };
              });
            }
          });
          
          
          
          

          newSocket.on('newMessage', (message) => {
            dispatch(addChatMessage(message));
          });

          setSocket(newSocket);
          
        });
      } catch (err) {
        initStartedRef.current = false;
        releaseRoomLock();
        toast.error(err?.message || 'Unable to initialize the meeting.');
        navigate('/login', { replace: true });
        console.error('❌ Error initializing meeting:', err);
        return;
      }
    }, [
      roomId,
      userIdGet,
      dispatch,
      location.state,
      consumeRemoteProducer,
      commitLocalMediaState,
      commitRemoteScreen,
      clearRemoteScreen,
      getRemoteProducerKey,
      meetingIdentity.valid,
      sessionId,
      syncLocalMediaState,
    ]);
const fetchAndConsumeRemoteStreams = useCallback(async () => {
  if (producerSyncPromiseRef.current) {
    return producerSyncPromiseRef.current;
  }

  const handler = mediaSoupHandler.current;
  if (
    !handler ||
    !socketRef.current?.connected ||
    !handler.isSignalingReady?.()
  ) {
    return undefined;
  }
  const consumersAtStart = new Set(handler.activeConsumers.keys());
  const syncPromise = (async () => {
    const { data: producers } = await getRoomProducers(roomId, sessionId);
    const currentProducerIds = new Set(
      (producers || []).map((producer) => String(producer.producerId || ''))
    );

    for (const producer of producers || []) {
      const producerId = String(producer.producerId || '');
      if (String(producer.sessionId) === String(sessionId) || !producerId) continue;
      if (handler.activeConsumers.has(producerId)) continue;

      try {
        await consumeRemoteProducer(producer);
      } catch (error) {
        if (classifyMediaApiError(error) !== 'stale-producer') {
          console.warn(
            `Unable to receive producer ${producerId}:`,
            error?.message || error
          );
        }
      }
    }

    for (const producerId of consumersAtStart) {
      if (!currentProducerIds.has(String(producerId))) {
        await handler.closeConsumerByProducer(producerId);
      }
    }
    return producers || [];
  })();

  producerSyncPromiseRef.current = syncPromise;
  try {
    return await syncPromise;
  } catch (error) {
    const classification = classifyMediaApiError(error);
    if (['authorization', 'rejoin'].includes(classification)) {
      handler.setSignalingReady?.(false);
      if (classification === 'rejoin') {
        void handler.onSignalingRejoinRequired?.({ error });
      } else {
        const activeSocket = socketRef.current;
        if (activeSocket?.connected) {
          activeSocket.disconnect();
          window.setTimeout(() => activeSocket.connect(), 100);
        }
      }
    } else if (
      !['stale-producer', 'meeting-ended'].includes(classification)
    ) {
      console.error('Failed fetching/consuming producers:', error);
    }
    return undefined;
  } finally {
    if (producerSyncPromiseRef.current === syncPromise) {
      producerSyncPromiseRef.current = null;
    }
  }
}, [consumeRemoteProducer, roomId, sessionId]);

useEffect(() => {
  if (!socket) return undefined;

  const handleParticipantRejoined = async ({
    sessionId: rejoinedSessionId,
    username,
  } = {}) => {
    const cleanSessionId = String(rejoinedSessionId || '').trim();
    if (!cleanSessionId || cleanSessionId === String(sessionId)) return;

    for (const eventKey of [...participantExitEventsRef.current]) {
      if (eventKey.startsWith(`${cleanSessionId}:`)) {
        participantExitEventsRef.current.delete(eventKey);
      }
    }

    toast.success(`${username || 'A participant'} has rejoined`);
    dispatch(updateParticipant({
      participantId: cleanSessionId,
      updates: {
        disconnected: false,
        disconnectedAt: null,
      },
    }));
    dispatch(updateParticipantNetworkStats({
      sessionId: cleanSessionId,
      connectionState: 'connecting',
      isOnline: true,
      sampledAt: Date.now(),
    }));

    try {
      await fetchAndConsumeRemoteStreams();
      await fetchRoomFromAPI();
    } catch (error) {
      console.error('Error handling participant-rejoined:', error);
    }
  };

  socket.on('participant-rejoined', handleParticipantRejoined);

  return () => {
    socket.off('participant-rejoined', handleParticipantRejoined);
  };
}, [dispatch, fetchAndConsumeRemoteStreams, fetchRoomFromAPI, sessionId, socket]);


  useEffect(() => {
    let cancelled = false;

    const waitForUser = async () => {
      while (!cancelled && !store.getState().auth.user) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      if (!cancelled) {
        await initMeeting();
      }
    };
  
    void waitForUser();

    return () => {
      cancelled = true;
      initStartedRef.current = false;

      const activeSocket = socketRef.current;
      try { activeSocket?.removeAllListeners?.(); } catch {}
      try { activeSocket?.io?.removeAllListeners?.(); } catch {}
      try { activeSocket?.disconnect?.(); } catch {}
      socketRef.current = null;

      try {
        mediaSoupHandler.current?.destroy?.();
      } catch {}
      mediaSoupHandler.current = null;
      if (window.mediaSoupHandler) window.mediaSoupHandler = null;

      const activeStream = store.getState().media?.localStream;
      activeStream?.getTracks?.().forEach((track) => {
        try { track.stop(); } catch {}
      });

      releaseRoomLock();
      dispatch(clearRoom());
      dispatch(clearMedia());
      dispatch(setLocalParticipantStatus(null));
    };
  }, [dispatch, initMeeting, releaseRoomLock, roomId, userIdGet]);
  // Save session join flag
  useEffect(() => {
    sessionStorage.setItem(`joined-${roomId}`, 'true');
  }, [roomId]);

  // Redirect to rejoin on tab switch back

  useEffect(() => {
    if (!socket) return;

    const applyAuthoritativeMediaState = (
      payload = {},
      { legacyKind = '' } = {}
    ) => {
      const cleanSessionId = String(payload.sessionId || '').trim();
      if (!cleanSessionId) return false;
      const incomingVersion = Math.max(
        0,
        Number(payload.mediaStateVersion || 0)
      );
      const currentVersion = Number(
        mediaStateVersionRef.current.get(cleanSessionId) || 0
      );
      // Combined state is allowed at the same revision after a compatibility
      // event, because it fills in the other media kind. A legacy single-kind
      // event at an already-applied revision is always stale.
      if (
        incomingVersion > 0 &&
        (incomingVersion < currentVersion ||
          (legacyKind && incomingVersion <= currentVersion))
      ) {
        return false;
      }
      if (incomingVersion > 0) {
        mediaStateVersionRef.current.set(cleanSessionId, incomingVersion);
      }

      const updates = {};
      if (
        !legacyKind &&
        typeof payload.audioEnabled === 'boolean'
      ) {
        updates.audioEnabled = payload.audioEnabled;
      }
      if (
        !legacyKind &&
        typeof payload.videoEnabled === 'boolean'
      ) {
        updates.videoEnabled = payload.videoEnabled;
      }
      if (legacyKind === 'audio' && typeof payload.isAudioEnabled === 'boolean') {
        updates.audioEnabled = payload.isAudioEnabled;
      }
      if (legacyKind === 'video' && typeof payload.isVideoEnabled === 'boolean') {
        updates.videoEnabled = payload.isVideoEnabled;
      }
      if (!Object.keys(updates).length) return false;

      dispatch(updateParticipant({
        participantId: cleanSessionId,
        updates: {
          ...updates,
          ...(incomingVersion > 0
            ? { mediaStateVersion: incomingVersion }
            : {}),
        },
      }));
      if (typeof updates.videoEnabled === 'boolean') {
        setRemoteVideoStatus((previous) => ({
          ...previous,
          [cleanSessionId]: updates.videoEnabled,
        }));
        if (updates.videoEnabled) {
          dispatch(updateParticipantNetworkStats({
            sessionId: cleanSessionId,
            connectionState: 'connecting',
            isOnline: true,
            sampledAt: Date.now(),
          }));
          window.setTimeout(() => {
            void fetchAndConsumeRemoteStreams();
          }, 100);
        }
      }
      return true;
    };

    const handleMediaState = (payload) => {
      applyAuthoritativeMediaState(payload);
    };
    const handleVideoToggle = ({
      sessionId: changedSessionId,
      isVideoEnabled,
      mediaStateVersion,
    } = {}) => {
      applyAuthoritativeMediaState(
        {
          sessionId: changedSessionId,
          isVideoEnabled,
          mediaStateVersion,
        },
        { legacyKind: 'video' }
      );
    };
    const handleAudioToggle = ({
      sessionId: changedSessionId,
      isAudioEnabled,
      mediaStateVersion,
    } = {}) => {
      applyAuthoritativeMediaState(
        {
          sessionId: changedSessionId,
          isAudioEnabled,
          mediaStateVersion,
        },
        { legacyKind: 'audio' }
      );
    };

    socket.on('participant-media-state', handleMediaState);
    socket.on('video-toggle', handleVideoToggle);
    socket.on('audio-toggle', handleAudioToggle);

    return () => {
      socket.off('participant-media-state', handleMediaState);
      socket.off('video-toggle', handleVideoToggle);
      socket.off('audio-toggle', handleAudioToggle);
    };
  }, [dispatch, fetchAndConsumeRemoteStreams, socket]);
  useEffect(() => {
    const handleReaction = ({ id, userId, sessionId: reactionSessionId, username, reaction, createdAt }) => {
      const cleanReaction = String(reaction || '').trim();
      if (!cleanReaction) return;
      const newReaction = {
        id: id || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        userId,
        sessionId: reactionSessionId,
        username,
        reaction: cleanReaction,
        createdAt: createdAt || new Date().toISOString(),
        offsetX: Math.round((Math.random() - 0.5) * 260),
        isEmoji: /^[\u{1F300}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]+$/u.test(cleanReaction),
      };
  
      setReactions(prev => [...prev, newReaction]);
  
      window.setTimeout(() => {
        setReactions(prev => prev.filter(r => r.id !== newReaction.id));
      }, 6000);
    };
  
    if (socket) {
      socket.on('reaction', handleReaction);
    }
  
    return () => {
      if (socket) socket.off('reaction', handleReaction);
    };
  }, [socket]);
  useEffect(() => {
    if (!socket) return undefined;

    const handleParticipantDisconnected = (payload = {}) => {
      const disconnectedSessionId = String(payload.sessionId || '');
      if (!disconnectedSessionId) return;

      // Keep the last remote frame/stream during the server grace period.
      // Only mark the tile offline; the participant-left event performs the
      // destructive MediaSoup cleanup if reconnection does not complete.
      dispatch(updateParticipantNetworkStats({
        sessionId: disconnectedSessionId,
        bitrateKbps: 0,
        connectionState: 'disconnected',
        strength: 0,
        isOnline: true,
        sampledAt: Date.now(),
      }));
      dispatch(updateParticipant({
        participantId: disconnectedSessionId,
        updates: {
          disconnected: true,
          disconnectedAt: payload.disconnectedAt || new Date().toISOString(),
        },
      }));
    };

    socket.on('participant-disconnected', handleParticipantDisconnected);

    return () => {
      socket.off('participant-disconnected', handleParticipantDisconnected);
    };
  }, [dispatch, socket]);

  useEffect(() => {
    if (!socket) return undefined;

    const handleParticipantLeft = (payload = {}) => {
      const leftUserId = payload.userId || payload.participantId;
      const leftSessionId = String(payload.sessionId || '');

      if (!leftSessionId) return;

      // The backend emits a canonical event plus a short-lived legacy alias.
      // De-duplicate both while still allowing a later disconnect-timeout event.
      const exitEventKey = `${leftSessionId}:${
        payload.leftAt || payload.reason || 'left'
      }`;

      if (participantExitEventsRef.current.has(exitEventKey)) return;
      participantExitEventsRef.current.add(exitEventKey);

      if (participantExitEventsRef.current.size > 200) {
        participantExitEventsRef.current.clear();
        participantExitEventsRef.current.add(exitEventKey);
      }

      if (payload.username) {
        toast.info(`${payload.username} has left the meeting`);
      }

      setRemoteStreamState((previous) => {
        const existing = previous[leftSessionId];

        try {
          existing?.stream?.getTracks?.().forEach((track) => track.stop());
        } catch {
          // Ignore tracks that were already stopped.
        }

        const next = { ...previous };
        delete next[leftSessionId];
        return next;
      });

      clearRemoteScreen(leftSessionId);
      remoteProducerStateRef.current.delete(
        getRemoteProducerKey(leftSessionId, 'audio')
      );
      remoteProducerStateRef.current.delete(
        getRemoteProducerKey(leftSessionId, 'video')
      );
      mediaStateVersionRef.current.delete(leftSessionId);

      setRemoteVideoStatus((previous) => {
        const next = { ...previous };
        delete next[leftSessionId];
        return next;
      });

      try {
        mediaSoupHandler.current?.closeConsumersBySession?.(leftSessionId);
      } catch (cleanupError) {
        console.warn('Remote consumer cleanup failed:', cleanupError);
      }

      dispatch(removeParticipant({
        userId: leftUserId,
        sessionId: leftSessionId,
      }));
      dispatch(removeParticipantNetworkStats({
        sessionId: leftSessionId,
      }));
    };

    socket.on('participant-left', handleParticipantLeft);
    socket.on('participantLeft', handleParticipantLeft);

    return () => {
      socket.off('participant-left', handleParticipantLeft);
      socket.off('participantLeft', handleParticipantLeft);
    };
  }, [clearRemoteScreen, dispatch, getRemoteProducerKey, socket]);
 
// ============ HOST ACTION LISTENERS ============
useEffect(() => {
  if (!socket) return undefined;

  const setRequestedLocalMedia = async (kind, enabled) => {
    const handler = mediaSoupHandler.current;
    if (handler) {
      if (kind === 'audio') await handler.toggleAudio(enabled);
      else await handler.toggleVideo(enabled);
      // The handler's onLocalMediaStateChange callback is the sole Redux +
      // acknowledged server-sync path.
      return;
    }

    const stream = store.getState().media?.localStream;
    const previous = localMediaStateRef.current[kind];
    stream?.getTracks?.()
      .filter((track) => track.kind === kind)
      .forEach((track) => {
        track.enabled = enabled;
      });
    localMediaStateRef.current = {
      ...localMediaStateRef.current,
      [kind]: enabled,
    };
    dispatch(kind === 'audio' ? toggleAudio(enabled) : toggleVideo(enabled));
    syncMediaStateToServer({
      previousByKind: { [kind]: previous },
    });
  };

  // Force mute audio handler
  const onForceMute = async () => {
    try {
      await setRequestedLocalMedia('audio', false);
    } catch (error) {
      toast.error(error?.message || 'Unable to mute the microphone');
      return;
    }

    toast.warning("🔇 Host has muted your microphone");
  };

  // Force disable video handler
  const onForceDisableVideo = async () => {
    try {
      await setRequestedLocalMedia('video', false);
    } catch (error) {
      toast.error(error?.message || 'Unable to turn the camera off');
      return;
    }

    toast.warning("📹 Host has turned off your camera");
  };

  // Request unmute handler
  const onRequestUnmute = () => {
    dispatch(showConfirm({
      message: "The host would like you to unmute your microphone.",
      onConfirm: async () => {
        try {
          await setRequestedLocalMedia('audio', true);
        } catch (error) {
          toast.error(error?.message || 'Unable to turn the microphone on');
        }
      }
    }));
  };

  // Request video handler
  const onRequestVideo = () => {
    dispatch(showConfirm({
      message: "The host would like you to turn on your camera.",
      onConfirm: async () => {
        try {
          await setRequestedLocalMedia('video', true);
        } catch (error) {
          toast.error(error?.message || 'Unable to turn the camera on');
        }
      }
    }));
  };

  // Host action error handler
  const onHostActionError = (data) => {
    console.error('❌ Host action error:', data);
    toast.error(data.message || 'Host action failed');
  };

  // Register all listeners
  socket.on('force-mute-audio', onForceMute);
  socket.on('force-disable-video', onForceDisableVideo);
  socket.on('request-unmute-audio', onRequestUnmute);
  socket.on('request-enable-video', onRequestVideo);
  socket.on('host-action-error', onHostActionError);

  // Cleanup
  return () => {
    socket.off('force-mute-audio', onForceMute);
    socket.off('force-disable-video', onForceDisableVideo);
    socket.off('request-unmute-audio', onRequestUnmute);
    socket.off('request-enable-video', onRequestVideo);
    socket.off('host-action-error', onHostActionError);
  };
}, [dispatch, socket, syncMediaStateToServer]);
  
  
  // Update active connections count when producers/consumers change
  useEffect(() => {
    if (!mediaSoupHandler.current) return;
    
    activeConnections.current = {
      producers: mediaSoupHandler.current.producers.size,
      consumers: mediaSoupHandler.current.activeConsumers.size
    };
  }, [mediaSoupHandler.current?.producers.size, mediaSoupHandler.current?.activeConsumers.size]);
  useEffect(() => {
    if (!socket || !roomId) return;

    let cancelled = false;
    let timer = null;
    const pollRoomFallback = async () => {
      try {
        const response = await api.get(`/rooms/${roomId}`);
        const updatedRoom = response.data;
        const currentRoom = store.getState().room.currentRoom;
  
        // Extract only key parts for comparison
        const compareFields = (room) => ({
          participants: room.participants?.map(p => ({
            userId: p.userId || p.user?._id || p.user,
            sessionId: p.sessionId,
            status: p.status,
            audioEnabled: p.audioEnabled,
            videoEnabled: p.videoEnabled
          })),
          isActive: room.isActive,
          host: room.host?._id || room.host,
          roomId: room.roomId,
        });
  
        const currentTrimmed = compareFields(currentRoom || {});
        const updatedTrimmed = compareFields(updatedRoom);
  
        // ✅ Only dispatch if core values changed
        if (!isEqual(currentTrimmed, updatedTrimmed)) {
          dispatch(setRoom(updatedRoom));
        }
  
        // Join requests are emitted once by the join flow. Polling only refreshes
        // room state and must never create repeated moderator alerts/sounds.
      } catch (err) {
        console.error("Polling failed:", err);
      } finally {
        if (!cancelled) {
          // Socket events are authoritative. A slow reconciliation protects
          // against a missed event without hammering the API; disconnected
          // sockets poll faster only until their namespace reconnects.
          timer = window.setTimeout(
            pollRoomFallback,
            socket.connected ? 60_000 : 8_000
          );
        }
      }
    };

    timer = window.setTimeout(
      pollRoomFallback,
      socket.connected ? 60_000 : 1_000
    );
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [socket, roomId, userIdGet, sessionId, dispatch]);
  
  
  
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      meetingContainerRef.current?.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };
  const shareLink = `${window.location.origin}/join-room?roomIdGet=${roomId}`;

  useEffect(() => {
    if (!socket) return;
  
    const handleHandRaised = ({ sessionId, isRaised }) => {
      dispatch(updateParticipant({
        participantId: sessionId,
        updates: { isRaised },
      }));
    };

    socket.on('handRaised', handleHandRaised);
  
    return () => {
      socket.off('handRaised', handleHandRaised);
    };
  }, [socket, dispatch]);
  
  // Add this useEffect to handle recording status updates

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);


// Add to your useEffect for handling screen sharing
// useEffect(() => {
//   if (!socket) return;

//   socket.on('screen-sharing', async ({ userId, isSharing, producerId, sessionId }) => {
//     if (isSharing) {
//       try {
//         const consumer = await mediaSoupHandler.current.consume(producerId);
//         dispatch(addRemoteScreen({
//           userId: sessionId, // Use sessionId as key
//           stream: consumer.track
//         }));
//       } catch (error) {
//         console.error('Failed to consume screen share:', error);
//       }
//     } else {
//       dispatch(removeRemoteScreen({ userId: sessionId }));
//     }
//   });

//   return () => {
//     socket.off('screen-sharing');
//   };
// }, [socket, dispatch]);


// In MeetingRoom.jsx
useEffect(() => {
  if (!socket) return;

  const handleScreenSharing = async ({
    roomId: sharedRoomId,
    isSharing,
    producerId,
    sessionId: sharingSessionId,
  } = {}) => {
    if (sharedRoomId && String(sharedRoomId) !== String(roomId)) return;
    // Skip if this is our own screen share
    const remoteSessionId = String(sharingSessionId || '');
    if (!remoteSessionId || remoteSessionId === String(sessionId)) return;

    if (isSharing) {
      try {
        // The same producer is normally already announced by `newProducer`.
        // Reuse the unified idempotent path instead of consuming and updating
        // React state a second time.
        await consumeRemoteProducer({
          producerId,
          sessionId: remoteSessionId,
          mediaType: 'screen',
        });
      } catch (error) {
        console.error('Failed to consume screen share:', error);
      }
    } else {
      clearRemoteScreen(remoteSessionId, producerId);
    }
  };

  socket.on('screen-sharing', handleScreenSharing);
  return () => {
    socket.off('screen-sharing', handleScreenSharing);
  };
}, [clearRemoteScreen, consumeRemoteProducer, roomId, sessionId, socket]);

useEffect(() => {
  if (!socket) return;

  const belongsToCurrentRoom = (payload = {}) =>
    !payload.roomId ||
    String(payload.roomId) === String(currentRoom?.roomId || roomId);
  const persistRoomRecordingState = (isRecording, decisionState = '') => {
    const latestRoom = store.getState().room.currentRoom;
    if (!latestRoom || String(latestRoom.roomId) !== String(roomId)) return;
    dispatch(setRoom({
      ...latestRoom,
      isRecording: Boolean(isRecording),
      ...(decisionState
        ? {
            recordingDecision: {
              ...(latestRoom.recordingDecision || {}),
              state: decisionState,
            },
          }
        : {}),
    }));
  };
  const applyRecordingState = (
    isRecording,
    payload = {},
    { notify = true, decisionState = '' } = {}
  ) => {
    if (!belongsToCurrentRoom(payload)) return;
    const wasRecording = recordingStateRef.current;
    recordingStateRef.current = Boolean(isRecording);
    dispatch(toggleRecording(Boolean(isRecording)));
    persistRoomRecordingState(isRecording, decisionState);

    if (isRecording) {
      setRecordPrompt(null);
      setRecordingRequiredSignal(null);
    }
    if (RECORDING_UI_ENABLED && notify && wasRecording !== Boolean(isRecording)) {
      toast.info(isRecording ? 'Class recording started.' : 'Class recording stopped.', {
        toastId: `recording-state-${roomId}-${isRecording ? 'on' : 'off'}`,
        autoClose: 3000,
      });
    }
  };
  const handleRecordingStatus = (payload = {}) =>
    applyRecordingState(Boolean(payload.isRecording), payload);
  const handleRecordingStarted = (payload = {}) =>
    applyRecordingState(true, payload, { decisionState: 'recording' });
  const handleRecordingStopped = (payload = {}) => {
    const captureFailed =
      String(payload.status || '').toLowerCase() === 'failed' ||
      ['capture-aborted', 'capture-stalled', 'capture-timeout'].includes(
        String(payload.reason || '').toLowerCase()
      );
    applyRecordingState(false, payload, {
      decisionState: captureFailed ? 'pending' : 'completed',
    });
  };
  const handleRecordingRequired = (payload = {}) => {
    if (
      !RECORDING_UI_ENABLED ||
      !belongsToCurrentRoom(payload) ||
      recordingStateRef.current
    ) return;
    const promptKey = `${roomId}:${sessionId}`;
    dismissedRecordingPromptsRef.current.delete(promptKey);
    setRecordingRequiredSignal({
      roomId: String(roomId),
      receivedAt: Date.now(),
    });
  };
  const handleRecordingUploaded = (payload = {}) => {
    if (!belongsToCurrentRoom(payload)) return;
    notifyRecordingStop(roomId, { success: true });
    setRecordPrompt(null);
    setRecordingRequiredSignal(null);
  };
  const handleRecordingUploadFailed = (payload = {}) => {
    if (!belongsToCurrentRoom(payload)) return;
    notifyRecordingStop(roomId, { success: false, message: recordingUiError({ message: payload.error || 'Recording save needs a retry.' }) });
    if (!RECORDING_UI_ENABLED) {
      console.warn(
        'Recording upload failed while recording UI is hidden:',
        payload.error || payload
      );
      return;
    }
    toast.error(
      recordingUiError({ message: payload.error || 'Class recording was captured, but saving needs a retry.' }),
      {
        toastId: `recording-upload-failed-${roomId}`,
        autoClose: 7000,
      }
    );
  };

  const handleAutomaticPolicy = (payload = {}) => {
    if (!belongsToCurrentRoom(payload) || !payload.automaticRecording) return;
    const latestRoom = store.getState().room.currentRoom;
    if (!latestRoom || String(latestRoom.roomId) !== String(roomId)) return;
    const automatic = payload.automaticRecording;
    dispatch(setRoom({ ...latestRoom,
      settings: { ...(latestRoom.settings || {}), automaticRecordingEnabled: automatic.configuredEnabled === true },
      recordingDecision: { ...(latestRoom.recordingDecision || {}), state: automatic.state },
    }));
    window.dispatchEvent(new CustomEvent('meeting-recording-policy-updated', { detail: { roomId } }));
  };
  socket.on('recording:automatic-policy', handleAutomaticPolicy);
  socket.on('recordingStatus', handleRecordingStatus);
  socket.on('recording:started', handleRecordingStarted);
  socket.on('recording:stopped', handleRecordingStopped);
  socket.on('recording:required', handleRecordingRequired);
  socket.on('recording:uploaded', handleRecordingUploaded);
  socket.on('recording:upload-failed', handleRecordingUploadFailed);

  return () => {
    socket.off('recording:automatic-policy', handleAutomaticPolicy);
    socket.off('recordingStatus', handleRecordingStatus);
    socket.off('recording:started', handleRecordingStarted);
    socket.off('recording:stopped', handleRecordingStopped);
    socket.off('recording:required', handleRecordingRequired);
    socket.off('recording:uploaded', handleRecordingUploaded);
    socket.off('recording:upload-failed', handleRecordingUploadFailed);
  };
}, [socket, dispatch, currentRoom?.roomId, roomId, sessionId]);

useEffect(() => {
  const handleCaptureError = (event) => {
    const detail = event?.detail || {};
    if (
      detail.roomId &&
      String(detail.roomId) !== String(currentRoom?.roomId || roomId)
    ) {
      return;
    }
    recordingStateRef.current = false;
    dispatch(toggleRecording(false));
    console.error(
      'Meeting recording capture failed:',
      detail.message || detail
    );
    if (RECORDING_UI_ENABLED) {
      setRecordingRequiredSignal({
        roomId: String(currentRoom?.roomId || roomId),
        receivedAt: Date.now(),
      });
      toast.error(
        recordingUiError({ message: detail.message || 'Recording save needs a retry. Use Resume upload to finish saving.' }),
        {
          toastId: `recording-capture-error-${currentRoom?.roomId || roomId}`,
          autoClose: 9000,
        }
      );
    }
  };
  window.addEventListener(
    'meeting-recording-capture-error',
    handleCaptureError
  );
  return () => window.removeEventListener(
    'meeting-recording-capture-error',
    handleCaptureError
  );
}, [currentRoom?.roomId, dispatch, roomId]);

useEffect(() => {
  if (!socket || !currentRoom?.roomId) return undefined;
  const offered = (payload = {}) => {
    if (payload.roomId !== currentRoom.roomId || currentRoom.meetingType === 'class') return;
    setExtensionOffer(payload);
  };
  const extended = async (payload = {}) => {
    if (payload.roomId !== currentRoom.roomId) return;
    setExtensionOffer(null);
    try { await fetchRoomFromAPI(); } catch {}
    toast.success(`Meeting extended by ${payload.extensionMinutes} minutes.`, {
      toastId: `meeting-extension-resolution-${currentRoom.roomId}`,
    });
  };
  const resolved = (payload = {}) => {
    if (String(payload.roomId || '') !== String(currentRoom.roomId)) return;
    // Admin and Staff can receive the same offer. Whichever moderator answers
    // first resolves it server-side, so every other open/busy dialog must close.
    setExtensionOffer(null);
    setExtensionBusy(false);
    if (String(payload.decision || '').toLowerCase() === 'declined') {
      toast.info('Meeting will end at the scheduled time.', {
        toastId: `meeting-extension-resolution-${currentRoom.roomId}`,
      });
    }
  };
  socket.on('meeting-extension-offer', offered);
  socket.on('meeting-extended', extended);
  socket.on('meeting-extension-resolved', resolved);
  return () => {
    socket.off('meeting-extension-offer', offered);
    socket.off('meeting-extended', extended);
    socket.off('meeting-extension-resolved', resolved);
  };
}, [socket, currentRoom?.roomId, currentRoom?.meetingType, fetchRoomFromAPI]);

// Classroom recording is checked immediately for every joined moderator.
// Admin/staff may dismiss their local reminder; faculty must start recording
// (or wait until another moderator starts it) before this modal can disappear.
useEffect(() => {
  if (!RECORDING_UI_ENABLED) {
    setRecordPrompt(null);
    setRecordingRequiredSignal(null);
    return;
  }

  const role = normalizeMeetingRole(
    user?.role || sessionStorage.getItem('userRole')
  );
  const promptKey = `${currentRoom?.roomId || ''}:${sessionId}`;
  const requiredByServer =
    recordingRequiredSignal?.roomId === String(currentRoom?.roomId || '');
  const shouldPrompt = shouldShowClassroomRecordingPrompt({
    room: currentRoom,
    role,
    participantStatus,
    recording,
    requiredByServer,
  });

  if (!shouldPrompt) {
    setRecordPrompt(null);
    return;
  }
  if (
    canDismissRecordingPrompt(role) &&
    dismissedRecordingPromptsRef.current.has(promptKey) &&
    !requiredByServer
  ) {
    setRecordPrompt(null);
    return;
  }

  setRecordPrompt((previous) =>
    previous?.roomId === currentRoom.roomId &&
    previous?.role === role
      ? previous
      : {
          roomId: currentRoom.roomId,
          role,
          promptedAt: Date.now(),
        }
  );
  toast.warning('This classroom is not being recorded. Start recording now.', {
    toastId: `class-recording-required-${currentRoom.roomId}-${sessionId}`,
    autoClose: 7000,
  });
}, [
  currentRoom?.roomId,
  currentRoom?.meetingType,
  currentRoom?.isActive,
  currentRoom?.meetingStatus,
  currentRoom?.meetingSatatus,
  currentRoom?.scheduledEndAt,
  currentRoom?.recordingDecision?.state,
  currentRoom?.isRecording,
  currentRoom?.access?.canRecord,
  participantStatus,
  recording,
  recordingRequiredSignal,
  sessionId,
  user?.role,
]);

useEffect(() => {
  if (!socket) return undefined;
  const handleDecision = ({ roomId: decisionRoomId, state } = {}) => {
    if (decisionRoomId && String(decisionRoomId) !== String(currentRoom?.roomId)) {
      return;
    }
    if (['recording', 'completed'].includes(state)) {
      setRecordPrompt(null);
      setRecordingRequiredSignal(null);
    }
  };
  socket.on('recording:decision', handleDecision);
  return () => socket.off('recording:decision', handleDecision);
}, [socket, currentRoom?.roomId]);

// Period-end stop is owned by the same browser that successfully started the
// MediaRecorder. Await final capture and verified saving before completing UI.
useEffect(() => {
  window.clearTimeout(autoRecordStopTimerRef.current);
  autoRecordStopTimerRef.current = null;

  if (!currentRoom?.roomId || !meetingRecorder.isOwnerForRoom(currentRoom.roomId)) {
    return undefined;
  }

  const endAt = getPeriodEndTimestamp(currentRoom);
  if (!endAt) return undefined;

  const stopOwnedRecording = async () => {
    try {
      notifyRecordingStop(currentRoom.roomId);
      const result = await meetingRecorder.stopIfActive(currentRoom.roomId);
      notifyRecordingStop(currentRoom.roomId, { success: result?.status === 'completed', message: 'Recording save needs a retry. Use Resume upload to finish saving.' });
      dispatch(toggleRecording(false));
      if (result?.status === 'completed') {
        toast.info('Period ended. Recording saved.');
      }
    } catch (error) {
      console.error('Period-end recording stop failed:', error);
      notifyRecordingStop(currentRoom.roomId, { success: false, message: recordingUiError(error) });
      toast.error(recordingUiError(error));
    }
  };

  if (endAt <= Date.now()) {
    void stopOwnedRecording();
    return undefined;
  }

  autoRecordStopTimerRef.current = window.setTimeout(
    () => void stopOwnedRecording(),
    Math.min(endAt - Date.now(), 2_147_000_000)
  );

  return () => {
    window.clearTimeout(autoRecordStopTimerRef.current);
    autoRecordStopTimerRef.current = null;
  };
}, [currentRoom, dispatch, recording]);

useEffect(() => {
  if (!socket || !currentRoom?.roomId) return undefined;

  const handleStopRequested = async ({ roomId: requestedRoomId, recordingId: requestedRecordingId } = {}) => {
    if (requestedRoomId !== currentRoom.roomId) return;
    if (!meetingRecorder.isOwnerForRoom(requestedRoomId) && !meetingRecorder.isSavingForRoom?.(requestedRoomId)) return;
    const ownedRecordingId = meetingRecorder.getStatus?.().recordingId || '';
    if (requestedRecordingId && ownedRecordingId && String(requestedRecordingId) !== String(ownedRecordingId)) return;
    try {
      notifyRecordingStop(requestedRoomId);
      const result = await meetingRecorder.stopIfActive(requestedRoomId);
      if (result?.status === 'completed') notifyRecordingStop(requestedRoomId, { success: true });
      else if (!result) {
        notifyRecordingStop(requestedRoomId, { success: false, message: 'Recording save needs a retry. Use Resume upload to finish saving.' });
        socket.emit('recording:stop-result', { roomId: requestedRoomId, recordingId: requestedRecordingId || ownedRecordingId, success: false });
      }
      dispatch(toggleRecording(false));
    } catch (error) {
      console.error('Requested recording stop failed:', error);
      notifyRecordingStop(requestedRoomId, { success: false, message: recordingUiError(error) });
      toast.error(recordingUiError(error));
      socket.emit('recording:stop-result', { roomId: requestedRoomId, recordingId: requestedRecordingId || ownedRecordingId, success: false });
    }
  };

  socket.on('recording:stop-requested', handleStopRequested);
  return () => socket.off('recording:stop-requested', handleStopRequested);
}, [socket, currentRoom?.roomId, dispatch]);
  

   


  
useEffect(() => {
  if (!isMobile()) return;

  // Bridge to native buttons
  const cleanup = registerPiPBridge?.({
    onToggleMic: () => window.mediaSoupHandler?.toggleAudio?.(),
    onHangup:    () => window.mediaSoupHandler?.leaveRoom?.(),
    onExpand:    () => {
      // Optional: come back from PiP
      exitPiPIf(mainVideoRef.current);
    },
  });

  return () => {
    try { cleanup?.(); } catch {}
  };
}, []);
  const emitMediaReceiveHealth = useCallback((report = {}, force = false) => {
    const activeSocket = socketRef.current;
    const sourceSessionId = String(report.sourceSessionId || '').trim();
    const mediaType = String(report.mediaType || 'video').toLowerCase();
    const status = String(report.status || 'connecting').toLowerCase();
    // The backend health contract intentionally accepts only terminal sample
    // states. "camera-off" is never reported as a failure and short-lived
    // connecting/recovering states remain local UI details.
    if (!['receiving', 'stalled', 'unavailable'].includes(status)) return;
    if (
      !activeSocket?.connected ||
      !sourceSessionId ||
      sourceSessionId === String(sessionId)
    ) {
      return;
    }

    const now = Date.now();
    const reportKey = `${sourceSessionId}:${mediaType}`;
    const previous = lastHealthEmitRef.current.get(reportKey);
    if (
      !force &&
      previous?.status === status &&
      now - previous.emittedAt < 5000
    ) {
      return;
    }
    lastHealthEmitRef.current.set(reportKey, {
      status,
      emittedAt: now,
    });
    activeSocket.emit('media-receive-health', {
      roomId,
      reporterSessionId: String(sessionId),
      reporterUserId: String(userIdGet || ''),
      reporterName: getMeetingDisplayName(user, 'Participant'),
      sourceSessionId,
      mediaType,
      status,
      bitrateKbps: Math.max(0, Math.round(Number(report.bitrateKbps) || 0)),
      packetsLostPercent: Math.max(0, Number(report.packetLoss) || 0),
      rttMs: Number.isFinite(Number(report.rttMs))
        ? Math.max(0, Math.round(Number(report.rttMs)))
        : null,
      observedAt: now,
    });
  }, [roomId, sessionId, user, userIdGet]);

  const handleRemoteVideoHealthChange = useCallback((health = {}) => {
    const sourceSessionId = String(health.sessionId || '').trim();
    if (!sourceSessionId || sourceSessionId === String(sessionId)) return;
    const previous = remoteMediaHealthRef.current[sourceSessionId] || {};
    const next = {
      ...previous,
      ...health,
      sourceSessionId,
      observedAt: Number(health.observedAt) || Date.now(),
    };
    remoteMediaHealthRef.current = {
      ...remoteMediaHealthRef.current,
      [sourceSessionId]: next,
    };
    emitMediaReceiveHealth(next, previous.status !== next.status);
  }, [emitMediaReceiveHealth, sessionId]);

  const retryRemoteVideo = useCallback(async (
    sourceSessionId,
    { automatic = false, notifySource = true } = {}
  ) => {
    const target = String(sourceSessionId || '').trim();
    if (!target || target === String(sessionId)) return 0;
    const retryKey = `video:${target}`;
    if (participantRetryPromisesRef.current.has(retryKey)) {
      return participantRetryPromisesRef.current.get(retryKey);
    }

    const recovery = (async () => {
      const recoveringHealth = {
        ...(remoteMediaHealthRef.current[target] || {}),
        sourceSessionId: target,
        sessionId: target,
        status: 'recovering',
        observedAt: Date.now(),
      };
      remoteMediaHealthRef.current = {
        ...remoteMediaHealthRef.current,
        [target]: recoveringHealth,
      };
      emitMediaReceiveHealth(recoveringHealth, true);

      let restored = await mediaSoupHandler.current?.retryConsumersForSession?.(
        target,
        'video'
      );
      // If no old consumer exists (for example its initial newProducer event
      // was lost), the authoritative producer list creates just that missing
      // consumer. Other participant tracks stay untouched.
      await fetchAndConsumeRemoteStreams();
      restored = Number(restored || 0);

      if (notifySource && socketRef.current?.connected) {
        socketRef.current.emit('request-media-retry', {
          roomId,
          requesterSessionId: String(sessionId),
          sourceSessionId: target,
          mediaType: 'video',
        });
      }
      if (!automatic) {
        toast.info(
          restored > 0
            ? 'This participant’s video connection was refreshed.'
            : 'Video producer check requested. Recovery continues automatically.',
          { toastId: `participant-video-retry-${target}`, autoClose: 1800 }
        );
      }
      return restored;
    })();

    participantRetryPromisesRef.current.set(retryKey, recovery);
    try {
      return await recovery;
    } finally {
      if (participantRetryPromisesRef.current.get(retryKey) === recovery) {
        participantRetryPromisesRef.current.delete(retryKey);
      }
    }
  }, [
    emitMediaReceiveHealth,
    fetchAndConsumeRemoteStreams,
    roomId,
    sessionId,
  ]);

  const retryRemoteAudio = useCallback(async (
    sourceSessionId,
    { notifySource = true } = {}
  ) => {
    const target = String(sourceSessionId || '').trim();
    if (!target || target === String(sessionId)) return 0;
    const retryKey = `audio:${target}`;
    if (participantRetryPromisesRef.current.has(retryKey)) {
      return participantRetryPromisesRef.current.get(retryKey);
    }

    const recovery = (async () => {
      let restored = await mediaSoupHandler.current?.retryConsumersForSession?.(
        target,
        'audio'
      );
      await fetchAndConsumeRemoteStreams();
      restored = Number(restored || 0);
      if (notifySource && socketRef.current?.connected) {
        socketRef.current.emit('request-media-retry', {
          roomId,
          requesterSessionId: String(sessionId),
          sourceSessionId: target,
          mediaType: 'audio',
        });
      }
      return restored;
    })();

    participantRetryPromisesRef.current.set(retryKey, recovery);
    try {
      return await recovery;
    } finally {
      if (participantRetryPromisesRef.current.get(retryKey) === recovery) {
        participantRetryPromisesRef.current.delete(retryKey);
      }
    }
  }, [
    fetchAndConsumeRemoteStreams,
    roomId,
    sessionId,
  ]);

  const retryVideoDelivery = useCallback((receiverSessionId) => {
    const receiver = String(receiverSessionId || '').trim();
    if (!receiver || !socketRef.current?.connected) return;
    setVideoDeliveryReports((previous) => ({
      ...previous,
      [receiver]: {
        ...(previous[receiver] || {}),
        reporterSessionId: receiver,
        status: 'recovering',
        observedAt: Date.now(),
      },
    }));
    socketRef.current.emit('request-receiver-media-retry', {
      roomId,
      receiverSessionId: receiver,
      sourceSessionId: String(sessionId),
      mediaType: 'video',
    });
  }, [roomId, sessionId]);

  const retryAudioDelivery = useCallback((receiverSessionId) => {
    const receiver = String(receiverSessionId || '').trim();
    if (!receiver || !socketRef.current?.connected) return;
    setAudioDeliveryReports((previous) => ({
      ...previous,
      [receiver]: {
        ...(previous[receiver] || {}),
        reporterSessionId: receiver,
        mediaType: 'audio',
        status: 'recovering',
        observedAt: Date.now(),
      },
    }));
    socketRef.current.emit('request-receiver-media-retry', {
      roomId,
      receiverSessionId: receiver,
      sourceSessionId: String(sessionId),
      mediaType: 'audio',
    });
  }, [roomId, sessionId]);

  useEffect(() => {
    if (!socket) return undefined;

    const handleHealthReport = (report = {}) => {
      if (
        report.sourceSessionId &&
        String(report.sourceSessionId) !== String(sessionId)
      ) {
        return;
      }
      const reporterSessionId = String(
        report.reporterSessionId || report.receiverSessionId || ''
      ).trim();
      if (!reporterSessionId) return;
      const participant = currentRoom?.participants?.find(
        (item) => String(item?.sessionId || '') === reporterSessionId
      );
      const observedValue = Number(report.observedAt);
      const parsedObservedAt = Number.isFinite(observedValue) && observedValue > 0
        ? observedValue
        : Number(new Date(report.observedAt || 0)) || Date.now();
      const updateDeliveryReports =
        String(report.mediaType || 'video').toLowerCase() === 'audio'
          ? setAudioDeliveryReports
          : setVideoDeliveryReports;
      updateDeliveryReports((previous) => ({
        ...previous,
        [reporterSessionId]: {
          ...previous[reporterSessionId],
          ...report,
          reporterSessionId,
          reporterName:
            report.reporterName ||
            getMeetingDisplayName(participant, 'Participant'),
          observedAt: parsedObservedAt,
        },
      }));
    };

    const handleMediaRetryRequested = async (request = {}) => {
      const sourceSessionId = String(request.sourceSessionId || '').trim();
      const receiverSessionId = String(
        request.receiverSessionId || request.requesterSessionId || ''
      ).trim();
      const mediaType = String(request.mediaType || 'video').toLowerCase();

      if (
        receiverSessionId === String(sessionId) &&
        sourceSessionId &&
        sourceSessionId !== String(sessionId)
      ) {
        if (mediaType === 'audio') {
          await retryRemoteAudio(sourceSessionId, {
            notifySource: false,
          });
        } else {
          await retryRemoteVideo(sourceSessionId, {
            automatic: true,
            notifySource: false,
          });
        }
      }
      if (
        sourceSessionId === String(sessionId) &&
        ((mediaType === 'audio' && audioEnabled) ||
          (mediaType !== 'audio' && videoEnabled))
      ) {
        await mediaSoupHandler.current?.refreshProducer?.(
          mediaType === 'audio' ? 'audio' : 'video'
        );
      }
    };

    socket.on('media-receive-health-report', handleHealthReport);
    socket.on('media-retry-requested', handleMediaRetryRequested);
    return () => {
      socket.off('media-receive-health-report', handleHealthReport);
      socket.off('media-retry-requested', handleMediaRetryRequested);
    };
  }, [
    audioEnabled,
    currentRoom?.participants,
    retryRemoteAudio,
    retryRemoteVideo,
    sessionId,
    socket,
    videoEnabled,
  ]);

  // Actual per-track WebRTC stats. No external ping is used, so bitrate, RTT,
  // jitter and packet loss describe the meeting media path itself.
  useEffect(() => {
    if (!socket || !mediaSoupHandler.current) return undefined;

    let isMounted = true;
    let timer = null;
    const INTERVAL_MS = 2000;

    const updateStats = async () => {
      if (!isMounted || !mediaSoupHandler.current) return;
      const startedAt = Date.now();
      try {
        const [remoteStats, localStats] = await Promise.all([
          mediaSoupHandler.current.getNetworkStatsSnapshot(),
          mediaSoupHandler.current.getLocalSendStats(),
        ]);
        if (!isMounted) return;

        for (const [remoteSessionId, stats] of Object.entries(remoteStats || {})) {
          const participant = currentRoom?.participants?.find(
            (item) => String(item?.sessionId || '') === String(remoteSessionId)
          );
          dispatch(updateParticipantNetworkStats({
            sessionId: remoteSessionId,
            ...stats,
            ...(participant?.disconnected === true
              ? {
                  connectionState: 'disconnected',
                  isOnline: true,
                  sampledAt: Date.now(),
                }
              : {}),
          }));

          const cameraEnabled =
            remoteVideoStatus?.[remoteSessionId] ??
            participant?.videoEnabled ??
            true;
          const elementHealth = remoteMediaHealthRef.current[remoteSessionId] || {};
          const videoStatus = cameraEnabled
            ? elementHealth.status ||
              stats?.media?.video?.status ||
              'connecting'
            : 'camera-off';
          emitMediaReceiveHealth({
            ...stats,
            ...elementHealth,
            sourceSessionId: remoteSessionId,
            mediaType: 'video',
            status: videoStatus,
          });

          const remoteAudioEnabled = participant?.audioEnabled ?? true;
          const audioStatus = remoteAudioEnabled
            ? stats?.media?.audio?.status || 'connecting'
            : 'paused';
          if (remoteAudioEnabled) {
            emitMediaReceiveHealth({
              ...stats,
              sourceSessionId: remoteSessionId,
              mediaType: 'audio',
              status: audioStatus,
              bitrateKbps: Number(stats?.audioBitrateKbps || 0),
            });
          }
        }

        if (sessionId) {
          dispatch(updateParticipantNetworkStats({
            sessionId: String(sessionId),
            ...localStats,
          }));
        }
        transportStatsRef.current = {
          send: {
            bitrate: Number(localStats?.bitrateKbps || 0),
            packetsSent: Number(localStats?.packetsSent || 0),
          },
          recv: {
            bitrate: Object.values(remoteStats || {}).reduce(
              (total, stats) => total + Number(stats?.bitrateKbps || 0),
              0
            ),
            packetsReceived: 0,
          },
        };
      } catch (error) {
        console.warn('WebRTC network stats update failed:', error);
      } finally {
        if (isMounted) {
          timer = window.setTimeout(
            updateStats,
            Math.max(250, INTERVAL_MS - (Date.now() - startedAt))
          );
        }
      }
    };

    void updateStats();
    return () => {
      isMounted = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [
    currentRoom?.participants,
    dispatch,
    emitMediaReceiveHealth,
    remoteVideoStatus,
    sessionId,
    socket,
  ]);


  // Duplicate 1-second room polling removed. The earlier 3-second fallback and
  // personalised socket events keep the room state current.

// MeetingRoom.jsx-ல் உள்ள Controls component-க்கு மேலே இதை சேர்க்கவும்:

const handleSilentRefresh = useCallback(async (quiet = false) => {
  if (mediaRecoveryPromiseRef.current) {
    return mediaRecoveryPromiseRef.current;
  }

  const recovery = (async () => {
    setIsRejoining(true);
    if (!quiet) toast.info('Repairing audio and video...');

    const activeSocket = socketRef.current;
    if (!activeSocket) throw new Error('Meeting socket is unavailable');
    if (!activeSocket.connected) {
      await new Promise((resolve, reject) => {
        const connected = () => {
          window.clearTimeout(timer);
          resolve();
        };
        const timer = window.setTimeout(() => {
          activeSocket.off('connect', connected);
          reject(new Error('Meeting socket reconnect timed out'));
        }, 12000);
        activeSocket.once('connect', connected);
        activeSocket.connect();
      });
    }

    const joinResult = await emitWithAckTimeout(activeSocket, 'joinRoom', {
      roomId,
      userId: userIdGet,
      sessionId,
      statusGet: 'check',
      deviceId: getOrCreateMeetingDeviceId(),
      rejoinStatus: sessionStorage.getItem('rejoinStatus'),
    });
    if (joinResult?.error) {
      throw new Error(joinResult.error);
    }
    if (!['admitted', 'presenting'].includes(String(joinResult?.status))) {
      throw new Error('Waiting for meeting admission');
    }

    const refreshedRoom = await fetchRoomFromAPI();
    if (
      refreshedRoom?.isActive === false ||
      refreshedRoom?.meetingStatus === 'ended' ||
      refreshedRoom?.meetingSatatus === 'ended'
    ) {
      closeOrReturnToDashboard(user);
      return;
    }

    await mediaSoupHandler.current?.recoverAfterSignalingReconnect?.({
      socket: activeSocket,
      roomId,
      userId: userIdGet,
    });
    await fetchAndConsumeRemoteStreams();
    activeSocket.emit('getRoomData', roomId);
    if (!quiet) toast.success('Audio and video restored');
  })();

  mediaRecoveryPromiseRef.current = recovery;
  try {
    return await recovery;
  } catch (error) {
    console.error('Silent media recovery error:', error);
    if (!quiet) {
      toast.error('Automatic repair is still retrying. Check your network.');
    }
    return undefined;
  } finally {
    if (mediaRecoveryPromiseRef.current === recovery) {
      mediaRecoveryPromiseRef.current = null;
    }
    setIsRejoining(false);
  }
}, [
  fetchAndConsumeRemoteStreams,
  fetchRoomFromAPI,
  roomId,
  sessionId,
  user,
  userIdGet,
]);

useEffect(() => {
  if (!socket || !['admitted', 'presenting'].includes(String(participantStatus))) {
    return undefined;
  }
  const recoverAfterNetwork = () => {
    if (navigator.onLine) void handleSilentRefresh(true);
  };
  let lastProducerReconcileAt = Date.now();
  const pollMediaHealth = async () => {
    if (!navigator.onLine || document.visibilityState === 'hidden') return;
    try {
      await mediaSoupHandler.current?.ensureMediaHealth?.();
      // Local WebRTC health can be checked frequently without server load.
      // Socket newProducer/producerClosed events are primary; the full REST
      // producer list is only a slow reconciliation fallback.
      const now = Date.now();
      if (now - lastProducerReconcileAt >= 60_000) {
        lastProducerReconcileAt = now;
        await fetchAndConsumeRemoteStreams();
      }
    } catch (error) {
      console.warn('Background media health check failed:', error);
    }
  };
  window.addEventListener('online', recoverAfterNetwork);
  const timer = window.setInterval(() => void pollMediaHealth(), 8000);
  return () => {
    window.removeEventListener('online', recoverAfterNetwork);
    window.clearInterval(timer);
  };
}, [
  socket,
  participantStatus,
  fetchAndConsumeRemoteStreams,
  handleSilentRefresh,
]);

  const showControlClock =(status)=>{
    if(status === 'chat')
    {
setShowChat(prev => {
      const next = !prev;
      return next;
    });
setShowParticipants(false)
setShowMeetingInfo(false)
setShowBible(false)
    }
      if(status === 'participants')
      {
setShowChat(false)
setShowParticipants(prev => !prev)
setShowMeetingInfo(false)
setShowBible(false)
      }
        if(status === 'info')
        {
setShowChat(false)
setShowParticipants(false)
setShowMeetingInfo(prev => !prev)
setShowBible(false)
        }
        if(status === 'bible')
        {
setShowChat(false)
setShowParticipants(false)
setShowMeetingInfo(false)
setShowBible(prev => !prev)
        }

  }
//   useEffect(() => {
//   if (!isMobile()) return;
//   const onHide = async () => {
//     if (document.visibilityState === 'hidden') {
//       await enterPiPFor(mainVideoRef.current);
//     }
//   };
//   document.addEventListener('visibilitychange', onHide);
//   return () => document.removeEventListener('visibilitychange', onHide);
// }, []);

useEffect(() => {
  let t = null;

  const handleVisibilitySafe = () => {
    // ignore early events during launch splash/transition
    if (launchGuardRef.current) return;

    clearTimeout(t);
    t = setTimeout(async () => {
      // Only do light behavior on visibility; never navigate/minimize on show/hide
      if (document.visibilityState === 'hidden') {
        // Enter PiP only on mobile to mimic Meet; skip on desktop
        if (isMobile()) {
          try { await enterPiPFor(mainVideoRef.current); } catch {}
        }
      } else {
        // Optionally resume UI bits; do NOT auto navigate or exit here
        // e.g., await exitPiPIf(mainVideoRef.current);
      }
    }, 150);
  };

  document.addEventListener('visibilitychange', handleVisibilitySafe);
  window.addEventListener('pagehide', handleVisibilitySafe);
  window.addEventListener('blur', handleVisibilitySafe);

  return () => {
    clearTimeout(t);
    document.removeEventListener('visibilitychange', handleVisibilitySafe);
    window.removeEventListener('pagehide', handleVisibilitySafe);
    window.removeEventListener('blur', handleVisibilitySafe);
  };
}, []);

useEffect(() => {
  const onEnter = () => console.log('✅ Entered PiP');
  const onLeave = () => console.log('↩️ Left PiP');

  document.addEventListener('enterpictureinpicture', onEnter);
  document.addEventListener('leavepictureinpicture', onLeave);
  return () => {
    document.removeEventListener('enterpictureinpicture', onEnter);
    document.removeEventListener('leavepictureinpicture', onLeave);
  };
}, []);

const startPromptRecording = async () => {
  if (recordPromptBusy || !currentRoom?.roomId) return;
  setRecordPromptBusy(true);
  try {
    await api.post(`/recordings/${encodeURIComponent(currentRoom.roomId)}/decision`, {
      decision: 'record',
      sessionId,
    });
    const result = await meetingRecorder.start({
      roomId: currentRoom.roomId,
      title: currentRoom.title,
    });
    if (!result?.recordingId) throw new Error('Recording did not start');
    recordingStateRef.current = true;
    dispatch(toggleRecording(true));
    setRecordPrompt(null);
    setRecordingRequiredSignal(null);
    if (RECORDING_UI_ENABLED) toast.success('Class recording started.');
  } catch (error) {
    // A different moderator may have accepted the same prompt first. In that
    // case the server recording lock is authoritative and this browser simply
    // follows the shared status.
    if (error.response?.status === 409) {
      recordingStateRef.current = true;
      dispatch(toggleRecording(true));
      setRecordPrompt(null);
      setRecordingRequiredSignal(null);
      if (RECORDING_UI_ENABLED) {
        toast.info('Another moderator has already started the recording.');
      }
    } else {
      if (RECORDING_UI_ENABLED) {
        toast.error(recordingUiError(error));
      } else {
        console.error('Recording start failed while recording UI is hidden:', error);
      }
    }
  } finally {
    setRecordPromptBusy(false);
  }
};

const dismissPromptRecording = () => {
  if (!recordPrompt || !canDismissRecordingPrompt(recordPrompt.role)) return;
  dismissedRecordingPromptsRef.current.add(
    `${recordPrompt.roomId}:${sessionId}`
  );
  setRecordingRequiredSignal(null);
  setRecordPrompt(null);
};

const respondToExtension = async (minutes) => {
  if (extensionBusy || !currentRoom?.roomId) return;
  setExtensionBusy(true);
  try {
    const { data } = await api.post(
      `/rooms/${encodeURIComponent(currentRoom.roomId)}/extend`,
      { minutes }
    );
    setExtensionOffer(null);
    if (minutes > 0) {
      dispatch(setRoom({
        ...currentRoom,
        scheduledEndAt: data?.data?.scheduledEndAt || currentRoom.scheduledEndAt,
      }));
      toast.success(`Meeting extended by ${minutes} minutes.`, {
        toastId: `meeting-extension-resolution-${currentRoom.roomId}`,
      });
    }
  } catch (error) {
    if (
      error.response?.status === 409 &&
      error.response?.data?.code === 'MEETING_EXTENSION_ALREADY_RESOLVED'
    ) {
      setExtensionOffer(null);
      toast.info('Another administrator or staff member already answered this extension request.', {
        toastId: `meeting-extension-resolution-${currentRoom.roomId}`,
      });
    } else {
      toast.error(error.response?.data?.message || error.message || 'Unable to update meeting time');
    }
  } finally {
    setExtensionBusy(false);
  }
};

const hasUnreadChatSignal = unreadMessages > 0 || Boolean(lastIncomingChat);

return (
    <div
      ref={meetingContainerRef}
      className="meeting-room relative flex flex-col h-screen w-full bg-gray-900 text-white overflow-hidden"
      style={{ height: '100dvh' }}
    >
      {/* Top bar */}
      <div className="meeting-room-topbar flex items-center justify-between px-2 sm:px-4 py-2 bg-[#3c4043]">
        {/* Left side - Logo and Seminary Name */}
        <div className="flex items-center">
          <span className="flex items-center text-lg sm:text-xl font-medium gap-2">
            <img src={logo} alt="Logo" className="h-6 sm:h-8 w-auto" />
            <span className="hidden xl:inline">SHEPHERD BIBLICAL SEMINARY</span>
          </span>
        </div>

        {/* Center content - Only shown on mobile */}
        <div className="md:hidden flex-1 flex flex-col justify-center items-center px-2 min-w-0">
          <span className="text-base sm:text-lg font-medium truncate max-w-full">{currentRoom?.title}</span>
          {periodRemaining != null && (
            <span
              className={`max-w-full text-center font-mono leading-tight tabular-nums ${periodRemaining <= 180 ? 'text-sm font-black text-amber-200 sm:text-base' : `text-xs font-semibold sm:text-sm ${periodRemaining <= 600 ? 'text-amber-300' : 'text-emerald-300'}`}`}
              data-meeting-scheduled-time={periodRemaining <= 180 ? 'final-three-minutes' : 'normal'}
            >
              {periodRemaining > 0
                ? `Scheduled time left ${formatRemaining(periodRemaining)}`
                : 'Scheduled time completed · Ending automatically…'}
            </span>
          )}
          <MeetingLiveClock compact />
        </div>

        {/* Center content - Desktop */}
        <div className="meeting-room-title hidden min-w-0 flex-1 md:flex flex-col items-center leading-tight">
          <span className="max-w-full truncate text-xl font-medium" title={currentRoom?.title}>{currentRoom?.title}</span>
          {periodRemaining != null && (
            <span
              className={`font-mono tabular-nums ${periodRemaining <= 180 ? 'text-lg font-black text-amber-200' : `text-sm font-semibold ${periodRemaining <= 600 ? 'text-amber-300' : 'text-emerald-300'}`}`}
              data-meeting-scheduled-time={periodRemaining <= 180 ? 'final-three-minutes' : 'normal'}
            >
              {periodRemaining > 0
                ? `Scheduled time left ${formatRemaining(periodRemaining)}`
                : 'Scheduled time completed · Ending automatically…'}
            </span>
          )}
        </div>

        {/* Right side - Refresh Button & Notifications */}
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="hidden md:block">
            <MeetingLiveClock />
          </div>
          {canUseLocalPresentation && (
            <button
              type="button"
              onClick={() => {
                setBlackboardMinimizeRequestId((value) => value + 1);
                setShowChat(false);
                setShowParticipants(false);
                setShowBible(false);
                setShowMeetingInfo(false);
                setShowLocalPresentation(true);
              }}
              className="flex items-center gap-1.5 rounded-lg border border-orange-300/30 bg-orange-500/15 px-2 py-1.5 text-orange-100 transition hover:bg-orange-500/25 sm:px-3"
              title="Open local slides, PDF, documents, text, tables, or office-file guidance"
              aria-label="Open local presentation studio"
            >
              <FaFilePowerpoint size={15} />
              <span className="hidden text-xs font-bold lg:inline">Present file</span>
            </button>
          )}
          <select
            value={viewMode}
            onChange={(event) => {
              setViewMode(event.target.value);
              localStorage.setItem(MEETING_VIEW_MODE_KEY, event.target.value);
            }}
            className="max-w-[92px] rounded-lg border border-gray-500 bg-gray-700 px-2 py-1.5 text-xs text-white sm:max-w-none"
            title="Meeting layout"
          >
            <option value="spotlight">Spotlight</option>
            <option value="tiles">Tiles</option>
            <option value="sidebar">Sidebar</option>
          </select>
          
          {/* 🔄 REFRESH BUTTON (Chat & Participants-க்கு முன்னால்) */}
          <button
            onClick={handleSilentRefresh}
            disabled={isRejoining}
            className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 transition ${isRejoining ? 'opacity-50 cursor-not-allowed' : ''}`}
            title="Refresh Connection"
          >
            <FaSyncAlt size={14} className={`text-white ${isRejoining ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline text-sm font-medium">Refresh</span>
          </button>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Chat notification */}
            <button
              type="button"
              className={`relative cursor-pointer rounded-full p-1.5 transition hover:bg-gray-700 sm:p-2 ${
                hasUnreadChatSignal
                  ? 'bg-amber-500/20 ring-2 ring-amber-300 motion-safe:animate-pulse motion-reduce:animate-none'
                  : ''
              }`}
              aria-label={hasUnreadChatSignal
                ? `Open chat. ${unreadMessages || 1} new message${(unreadMessages || 1) === 1 ? '' : 's'}.`
                : 'Open meeting chat'}
              onClick={() => {
                if (hasUnreadChatSignal && lastIncomingChat) {
                  openMeetingChat(lastIncomingChat);
                } else if (showChat) showControlClock('chat');
                else openMeetingChat(lastIncomingChat);
              }}
            >
              <svg className={`w-5 h-5 sm:w-6 sm:h-6 ${hasUnreadChatSignal ? 'text-amber-200' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {unreadMessages > 0 && (
                <span
                  className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white motion-safe:animate-bounce motion-reduce:animate-none sm:-right-2 sm:-top-2 sm:h-5 sm:min-w-5 sm:text-xs"
                  title={`You have ${unreadMessages} unread ${unreadMessages === 1 ? 'message' : 'messages'}`}
                  role="status"
                  aria-live="polite"
                >
                  {unreadMessages > 99 ? '99+' : unreadMessages}
                </span>
              )}
            </button>

            {/* Join requests notification for the admin or assigned faculty co-host */}
            {(currentRoom?.access?.canAdmit === true || sessionStorage.getItem('canAdmit') === 'true') && (
              <button
                type="button"
                aria-label={pendingRequests > 0 ? `Open participants. ${pendingRequests} waiting to join.` : 'Open participants and join requests'}
                className="relative cursor-pointer p-1.5 sm:p-2 hover:bg-gray-700 rounded-full transition"
                onClick={() => {
                  setBlackboardMinimizeRequestId((value) => value + 1);
                  showControlClock('participants');
                }}
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                {pendingRequests > 0 && (
                  <span className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-blue-500 text-white text-[10px] sm:text-xs rounded-full h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center" title={`You have ${pendingRequests} waiting request`}>
                    {pendingRequests > 99 ? '99+' : pendingRequests}
                  </span>
                )}
              </button>
            )}
          </div>

      
        </div>
      </div>

      {hasUnreadChatSignal && !showChat && (
        <div
          className="pointer-events-none absolute right-3 top-16 z-[180] sm:right-5 sm:top-20"
          role="status"
          aria-live="assertive"
          aria-atomic="true"
        >
          <button
            type="button"
            onClick={() => openMeetingChat(lastIncomingChat)}
            className="pointer-events-auto flex items-center gap-3 rounded-xl border border-amber-300 bg-amber-400 px-3 py-2 text-left text-gray-950 shadow-2xl transition hover:bg-amber-300 motion-safe:animate-pulse motion-reduce:animate-none sm:px-4 sm:py-3"
            data-meeting-new-chat-alert="true"
          >
            <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-900 text-lg text-white">
              💬
              {unreadMessages > 0 && (
                <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
                  {unreadMessages > 99 ? '99+' : unreadMessages}
                </span>
              )}
            </span>
            <span>
              <span className="block text-sm font-black">
                {lastIncomingChat?.isPrivate ? 'New private message' : 'New message · Chat'}
              </span>
              <span className="block max-w-[220px] truncate text-xs font-semibold">
                {lastIncomingChat?.username
                  ? `From ${lastIncomingChat.username} · click to open`
                  : 'Click to open meeting chat'}
              </span>
            </span>
          </button>
        </div>
      )}

      <MeetingEndCountdownAlert
        remainingSeconds={periodRemaining}
        warningKey={threeMinuteWarning?.key || ''}
        meetingTitle={currentRoom?.title || ''}
        onDismissWarning={dismissThreeMinuteWarning}
      />

      {/* {visibleMeetingObservers.length > 0 && (
        <div
          className="flex shrink-0 items-center justify-center gap-2 border-y border-amber-300/25 bg-amber-950/95 px-3 py-2 text-center text-xs font-medium text-amber-100"
          role="status"
          aria-live="polite"
        >
          <FaEye className="shrink-0 text-amber-300" aria-hidden="true" />
          <span>
            {visibleMeetingObservers.length === 1
              ? `${
                  visibleMeetingObservers[0]?.displayName ||
                  visibleMeetingObservers[0]?.role ||
                  'Admin/Staff'
                } is`
              : `${visibleMeetingObservers.length} Admin/Staff observers are`}{' '}
            viewing this meeting live in visible read-only mode.
          </span>
        </div>
      )} */}

      <div className="relative flex min-h-0 flex-1 overflow-hidden w-full">
        {remotePresentation && (
          <div
            className="pointer-events-none absolute left-1/2 top-3 z-[70] max-w-[88vw] -translate-x-1/2 rounded-full border border-indigo-300/35 bg-slate-950/90 px-4 py-2 text-center text-xs font-semibold text-indigo-100 shadow-xl backdrop-blur-md"
            role="status"
            aria-live="polite"
            data-remote-presentation-banner={remotePresentation.deckId}
          >
            {remotePresentation.presenterName || 'Presenter'} is sharing {remotePresentation.fileName || 'a local presentation'}
            {remotePresentation.slideCount > 0
              ? ` · slide ${remotePresentation.slideIndex + 1}/${remotePresentation.slideCount}`
              : ''}
          </div>
        )}
        <div className={`min-h-0 flex-1 w-full ${showChat || showParticipants ? 'hidden md:block' : ''}`}>
          <VideoGrid
            remoteStreams={memoizedRemoteStreams}
            remoteVideoStatus={memoizedRemoteVideoStatus}
            remoteScreens={remoteScreens}
            mainVideoRef={mainVideoRef}
            onRetryRemoteVideo={retryRemoteVideo}
            onRetryVideoDelivery={retryVideoDelivery}
            onRetryAudioDelivery={retryAudioDelivery}
            onRemoteVideoHealthChange={handleRemoteVideoHealthChange}
            videoDeliveryReports={videoDeliveryReports}
            audioDeliveryReports={audioDeliveryReports}
            viewMode={viewMode}
          />
        </div>
        {/* <div className={`flex-1 w-full ${showChat || showParticipants ? 'hidden md:block' : ''}`}>
  <THREEClassroom
    localStream={localStream}
    remoteStreams={memoizedRemoteStreams}
    remoteVideoStatus={memoizedRemoteVideoStatus}
    currentRoom={currentRoom}
    videoEnabled={videoEnabled}
  />
</div> */}
        {/* Meet-style reactions rise through the centre of the meeting. */}
        <div className="pointer-events-none absolute inset-0 z-40 overflow-hidden" aria-live="polite">
          {reactions.map((reaction) => (
            <div
              key={reaction.id}
              className="meeting-reaction-rise absolute bottom-[16%] left-1/2"
              style={{ '--reaction-offset-x': `${reaction.offsetX}px` }}
              title={reaction.username || undefined}
            >
              <div
                className={
                  reaction.isEmoji
                    ? 'text-5xl drop-shadow-xl sm:text-6xl'
                    : 'max-w-[80vw] rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-gray-900 shadow-xl sm:text-base'
                }
              >
                {reaction.reaction}
              </div>
            </div>
          ))}
        </div>

        {recording && (
          <RecordingIndicator recording={recording} />
        )}

        <AutomaticRecordingControl
          room={currentRoom} role={user?.role || sessionStorage.getItem('userRole')}
          participantStatus={participantStatus} sessionId={sessionId} recording={recording}
          onStarted={() => { recordingStateRef.current = true; dispatch(toggleRecording(true)); }}
        />

        <ParticipantStatusOverlay status={participantStatus} />

        {showChat && (
          <div className="absolute bottom-0 right-0 top-0 z-10 flex min-h-0 w-full flex-col overflow-hidden border-l border-gray-700 bg-gray-800 md:relative md:w-80">
            <div className="p-3 border-b border-gray-700 flex justify-between items-center bg-gray-800">
              <h3 className="font-medium">Chat</h3>
              <button onClick={() => setShowChat(false)} className="p-1 rounded-full hover:bg-gray-700 text-gray-300">
                <FaTimes />
              </button>
            </div>
            <Chat socket={socket} requestedTarget={requestedChatTarget} />
          </div>
        )}

        {showParticipants && (
          <div className="absolute bottom-0 right-0 top-0 z-10 flex min-h-0 w-full flex-col overflow-hidden border-l border-gray-700 bg-gray-800 md:relative md:w-80">
            <div className="flex shrink-0 items-center justify-between border-b border-gray-700 bg-gray-800 p-3">
              <h3 className="font-medium">Participants</h3>
              <button onClick={() => setShowParticipants(false)} className="p-1 rounded-full hover:bg-gray-700 text-gray-300">
                <FaTimes />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden" data-participant-panel-scroll-boundary="true">
              <Participants socket={socket} />
            </div>
          </div>
        )}

        {showBible && (
          <BiblePanel onClose={() => setShowBible(false)} />
        )}
        
        {participantStatus === 'admitted' && (
          <button
            onClick={toggleFullscreen}
            className="absolute top-4 right-4 p-2 rounded-full bg-gray-700 hover:bg-gray-600 z-20"
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {isFullscreen ? <FaCompress size={16} /> : <FaExpand size={16} />}
          </button>
        )}
        
        {showMeetingInfo && (
          <div className="absolute bottom-0 right-0 top-0 z-10 flex min-h-0 w-full flex-col overflow-hidden border-l border-gray-700 bg-gray-800 md:relative md:w-80">
            <div className="p-3 border-b border-gray-700 flex justify-between items-center bg-gray-800">
              <h2 className="text-lg font-semibold">Meeting details</h2>
              <button
                className="p-1 rounded-full hover:bg-gray-700 text-gray-300"
                onClick={() => setShowMeetingInfo(false)}
              >
                <FaTimes />
              </button>
            </div>
            <JoiningInfo shareLink={shareLink} />
          </div>
        )}

        {showMoreMenu && <ShowMore />}
      </div>

      <LocalPresentationStudio
        open={showLocalPresentation && canUseLocalPresentation}
        onClose={() => setShowLocalPresentation(false)}
        roomId={roomId}
        screenSharing={screenSharing}
        publishPresentationState={publishPresentationState}
        presentationClearRevision={presentationClearRevision}
        presentationSyncError={presentationSyncError}
      />

      <ScreenShareParticipantMonitor
        active={screenSharing}
        localStream={localStream}
        remoteStreams={memoizedRemoteStreams}
        currentRoom={currentRoom}
        currentSessionId={sessionId}
      />

      {/* Muted-speech alerts disabled. Keep the implementation for future use.
          To re-enable: uncomment the import above and this JSX block. */}
      {/*
      <ClassroomDiscipline
        room={currentRoom}
        socket={socket}
        user={user}
        participantStatus={participantStatus}
        audioEnabled={audioEnabled}
      />
      */}

      {/* Controls bar */}
      <div
        ref={controlsBarRef}
        className="relative z-[140] w-full bg-gray-800 py-2 px-2 sm:px-4 flex justify-center items-center gap-2 flex-wrap"
        style={{
          paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))',
        }}
      >
        <Controls
          remoteStreams={memoizedRemoteStreams}
          remoteVideoStatus={remoteVideoStatus}
          socket={socket}
          onToggleChat={() => {
            setBlackboardMinimizeRequestId((value) => value + 1);
            if (hasUnreadChatSignal && lastIncomingChat) {
              openMeetingChat(lastIncomingChat);
            } else {
              showControlClock('chat');
            }
          }}
          onToggleParticipants={() => {
            setBlackboardMinimizeRequestId((value) => value + 1);
            showControlClock('participants');
          }}
          onToggleBible={() => {
            setBlackboardMinimizeRequestId((value) => value + 1);
            showControlClock('bible');
          }}
          bibleOpen={showBible}
          onToggleMeetingInfo={() => {
            setBlackboardMinimizeRequestId((value) => value + 1);
            showControlClock('info');
          }}
          onOpenBlackboard={() => {
            setBlackboardOpenRequestId((value) => value + 1);
            setShowBlackboard(true);
          }}
          unreadMessages={unreadMessages}
          hasUnreadChat={hasUnreadChatSignal}
          showMore={() => setShowMoreMenu((prev) => !prev)}
        />
      </div>

      {currentRoom?.meetingType === 'class' && currentRoom?.roomId && socket && (
        <ClassBlackboard
          roomId={currentRoom.roomId}
          socket={socket}
          access={currentRoom.access || {}}
          forceVisible={showBlackboard}
          openRequestId={blackboardOpenRequestId}
          minimizeRequestId={blackboardMinimizeRequestId}
          onClose={() => setShowBlackboard(false)}
          participantDock={(
            <BlackboardParticipantDock
              localStream={localStream}
              remoteStreams={memoizedRemoteStreams}
              currentRoom={currentRoom}
              currentSessionId={sessionId}
            />
          )}
        />
      )}

      {RECORDING_UI_ENABLED && recordPrompt && (
        <div className="meeting-dialog-overlay fixed inset-0 z-[250] flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm">
          <div
            className="meeting-dialog relative w-full max-w-md rounded-2xl border border-white/15 bg-slate-900 p-6 text-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="record-class-title"
          >
            {canDismissRecordingPrompt(recordPrompt.role) && (
              <button
                type="button"
                onClick={dismissPromptRecording}
                disabled={recordPromptBusy}
                className="absolute right-3 top-3 rounded-full p-2 text-slate-300 hover:bg-white/10 hover:text-white disabled:opacity-50"
                aria-label="Close recording reminder"
              >
                <FaTimes />
              </button>
            )}
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-red-600 text-xl">●</span>
              <div>
                <h2 id="record-class-title" className="text-lg font-semibold">Class recording required</h2>
                <p className="text-sm text-slate-300">No moderator is recording this classroom yet.</p>
              </div>
            </div>
            <p className="mb-5 text-sm leading-6 text-slate-200">
              {canDismissRecordingPrompt(recordPrompt.role)
                ? 'Start the private class recording now. You may close this reminder; it does not mark the class as recorded.'
                : 'This period must be recorded. This reminder closes only after you or another moderator starts recording.'}
            </p>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              {canDismissRecordingPrompt(recordPrompt.role) && (
                <button
                  type="button"
                  disabled={recordPromptBusy}
                  onClick={dismissPromptRecording}
                  className="rounded-lg border border-slate-500 px-4 py-2 text-sm hover:bg-slate-800 disabled:opacity-50"
                >
                  Close for now
                </button>
              )}
              <button
                type="button"
                disabled={recordPromptBusy}
                onClick={startPromptRecording}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold hover:bg-red-500 disabled:opacity-50"
              >
                {recordPromptBusy ? 'Starting…' : 'Start recording'}
              </button>
            </div>
            <p className="mt-4 text-xs text-slate-400">
              Participants receive the standard recording notice while recording is active.
            </p>
          </div>
        </div>
      )}

      {extensionOffer && (
        <div className="meeting-dialog-overlay fixed inset-0 z-[260] flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm">
          <div className="meeting-dialog w-full max-w-md rounded-2xl border border-amber-300/30 bg-slate-900 p-6 text-white shadow-2xl" role="dialog" aria-modal="true">
            <h2 className="text-lg font-semibold">Extend this meeting?</h2>
            <p className="mt-2 text-sm leading-6 text-slate-300">
              This meeting will end in about 10 minutes. You are the active {extensionOffer.role || 'moderator'} with extension priority.
            </p>
            <div className="mt-5 grid grid-cols-3 gap-2">
              {(extensionOffer.options || [15, 30, 60]).map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  disabled={extensionBusy}
                  onClick={() => respondToExtension(Number(minutes))}
                  className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold hover:bg-emerald-500 disabled:opacity-50"
                >
                  +{minutes} min
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={extensionBusy}
              onClick={() => respondToExtension(0)}
              className="mt-3 w-full rounded-lg border border-slate-500 px-4 py-2 text-sm hover:bg-slate-800 disabled:opacity-50"
            >
              End at scheduled time
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingRoom;
